import React, { useState, useEffect } from 'react';
import { AIWriterModal } from '@/components/ai-writer/AIWriterModal';
import { AIWriterPreview } from '@/components/ai-writer/AIWriterPreview';
import { AIWriterPersonaModal } from '@/components/ai-writer/AIWriterPersonaModal';
import { ProductSelectionModal } from '@/components/qa/ProductSelectionModal';
import { QuestionWheelCategoryCards } from '@/components/sector/QuestionWheelCategoryCards';
import { PersonaBenefitMappingDialog } from '@/components/about-business/PersonaBenefitMappingDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  TrendingUp,
  Users,
  BookOpen,
  Package,
  RefreshCw,
  Info,
  Calendar,
  Tag,
  Sparkles,
  Building2,
  Target,
  FileText,
  User,
  ExternalLink,
  AlertCircle,
  Heart,
  ShoppingCart,
  Clock,
  Eye,
  PenTool,
  ChevronDown,
  ChevronRight,
  Trash2,
  HelpCircle,
  Plus,
  Play,
  Check,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Loader2,
  Star,
  List,
  Grid3x3,
  Globe,
  X
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { DemandWheel } from '@/components/sector/DemandWheel';
import { CategoryLeafDetail } from '@/components/sector/CategoryLeafDetail';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/auth-context';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';

interface SectorData {
  id: number;
  name: string;
  description: string;
  image_url?: string;
  image_prompt?: string;
  characteristics: {
    typical_age_range?: string;
    materials?: string[];
    price_range?: string;
    delivery_methods?: string[];
    certifications?: string[];
    [key: string]: any;
  };
  common_segments: Array<{
    name: string;
    description: string;
  }>;
  last_trends_update?: string;
}

interface SectorTrend {
  id: string;
  trend_title: string;
  trend_description: string;
  trend_source: string;
  trend_url?: string;
  trend_date: string;
  relevance_score: number;
  has_research?: boolean;
}

interface TrendResearch {
  id: string;
  trend_id: string;
  research_content: string;
  research_summary: string;
  key_findings: Array<{
    finding: string;
    importance: string;
    category: string;
  }>;
  sources: Array<{
    title: string;
    url?: string;
    domain?: string;
    publication?: string;
    type?: string;
    snippet?: string;
    excerpt?: string;
    date?: string;
    // Source classification fields
    sourceType?: string;
    qualityScore?: number;
    rankingScore?: number;
    citationWorthy?: boolean;
    domainAuthority?: string;
    flags?: string[];
    verified?: boolean;
  }>;
  // Quick access to citation-worthy sources
  citationSources?: Array<{
    title: string;
    url?: string;
    domain?: string;
    sourceType?: string;
    qualityScore?: number;
    citationWorthy?: boolean;
    domainAuthority?: string;
  }>;
  research_quality_score: number;
  word_count: number;
  research_depth: string;
  display_language: string;
  created_at: string;
}

interface SectorTopic {
  id: string;
  topic_title: string;
  description: string;
  relevance_score: number;
  status: string;
  topic_type: string;
  category: string;
  research_date: string;
  top_categories?: Array<{ count: number; category: string }>;
}

interface ApprovedResearch {
  sessionId: string;
  categoryName: string;
  attributesCount: number;
  createdAt: string;
  reviewedAt: string;
  reviewedByName: string;
}

export default function TheSector() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isContentManager = user?.role === 'site_content_manager';
  const [loading, setLoading] = useState(true);
  const [sectorData, setSectorData] = useState<SectorData | null>(null);
  const [trends, setTrends] = useState<SectorTrend[]>([]);
  const [topics, setTopics] = useState<SectorTopic[]>([]);
  const [personas, setPersonas] = useState<any[]>([]);
  const [translatedPersonas, setTranslatedPersonas] = useState<any[]>([]);
  const [isTranslatingPersonas, setIsTranslatingPersonas] = useState(false);
  const [siteSegments, setSiteSegments] = useState<any[]>([]);
  const [questionWheels, setQuestionWheels] = useState<any[]>([]);
  const [approvedResearch, setApprovedResearch] = useState<ApprovedResearch[]>([]);
  const [refreshingTrends, setRefreshingTrends] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [regeneratingPersonas, setRegeneratingPersonas] = useState<Set<string>>(new Set());
  const [loadingWheels, setLoadingWheels] = useState(false);
  const [loadingResearch, setLoadingResearch] = useState(false);

  // Benefit-Attribute Mappings state
  const [mappingsStats, setMappingsStats] = useState<{
    benefits: number;
    mappings: number;
    valueRanges: number;
  } | null>(null);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [extendingCoverage, setExtendingCoverage] = useState(false);

  // Sector Categories state
  const [taxonomyData, setTaxonomyData] = useState<any[]>([]);
  const [taxonomyBreadcrumb, setTaxonomyBreadcrumb] = useState<any[]>([]); // Track full category objects
  const [currentTaxonomyView, setCurrentTaxonomyView] = useState<any>(null);
  const [isEnrichingTaxonomy, setIsEnrichingTaxonomy] = useState(false);
  const [isEnrichingWithGoogleAds, setIsEnrichingWithGoogleAds] = useState(false);
  const [isReorganizingTaxonomy, setIsReorganizingTaxonomy] = useState(false);
  const [isResettingTaxonomy, setIsResettingTaxonomy] = useState(false);
  const [isGeneratingFromProducts, setIsGeneratingFromProducts] = useState(false);
  const [availableMarkets, setAvailableMarkets] = useState<{market_name: string, market_code: string}[]>([]);
  const [selectedMarket, setSelectedMarket] = useState('United States');
  const [enrichedMarkets, setEnrichedMarkets] = useState<{market: string, market_code: string, categories_count: number, last_enriched: string}[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<{language_code: string, market_name: string}[]>([]);
  const [displayLanguage, setDisplayLanguage] = useState<string>('original');
  const [categoryTranslations, setCategoryTranslations] = useState<Record<string, string>>({});
  const [viewMarket, setViewMarket] = useState<string>('United States');
  const isSuperAdmin = user?.role === 'super_admin';

  // Helper function to get category name (translated or original)
  const getCategoryDisplayName = (category: any) => {
    if (displayLanguage === 'original' || !categoryTranslations[category.id]) {
      return category.category_name;
    }
    return categoryTranslations[category.id];
  };

  // View mode state for categories tab
  const [categoryViewMode, setCategoryViewMode] = useState<'wheel' | 'tree'>('wheel');
  const [treeCategories, setTreeCategories] = useState<any[]>([]);
  const [loadingTreeView, setLoadingTreeView] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set()); // Nodes being lazy-loaded

  const [researchModal, setResearchModal] = useState<{
    open: boolean;
    title: string;
    perceptions: any[];
    loading: boolean;
  }>({
    open: false,
    title: '',
    perceptions: [],
    loading: false
  });

  // Translated perceptions for the modal
  const [translatedPerceptions, setTranslatedPerceptions] = useState<any[]>([]);
  const [isTranslatingPerceptions, setIsTranslatingPerceptions] = useState(false);

  const [topicResearchModal, setTopicResearchModal] = useState<{
    open: boolean;
    title: string;
    content: string;
    loading: boolean;
  }>({
    open: false,
    title: '',
    content: '',
    loading: false
  });

  const [trendDetailModal, setTrendDetailModal] = useState<{
    open: boolean;
    trend: SectorTrend | null;
  }>({
    open: false,
    trend: null
  });

  // Trend research state
  const [trendResearch, setTrendResearch] = useState<TrendResearch | null>(null);
  const [loadingTrendResearch, setLoadingTrendResearch] = useState(false);
  // Track which depth is being generated (null = none, 'quick'/'standard'/'comprehensive' = generating that depth)
  const [generatingTrendResearch, setGeneratingTrendResearch] = useState<string | null>(null);

  const [questionsModal, setQuestionsModal] = useState<{
    open: boolean;
    title: string;
    questions: any[];
    loading: boolean;
    cached: boolean;
    categoryName: string;
  }>({
    open: false,
    title: '',
    questions: [],
    loading: false,
    cached: false,
    categoryName: '',
  });

  const [generatingQuestions, setGeneratingQuestions] = useState<string | null>(null);
  
  const [aiWriterModal, setAiWriterModal] = useState<{
    open: boolean;
    topic: any;
  }>({ open: false, topic: null });
  
  const [aiWriterPersonaModal, setAiWriterPersonaModal] = useState<{
    open: boolean;
    persona: any;
  }>({ open: false, persona: null });

  const [generatedArticle, setGeneratedArticle] = useState<any>(null);

  // Research viewer dialog state
  const [researchDialog, setResearchDialog] = useState<{
    open: boolean;
    topicId: string | null;
    research: any;
  }>({ open: false, topicId: null, research: null });
  const [loadingResearchContent, setLoadingResearchContent] = useState(false);

  // Sector assignment state
  const [availableSectors, setAvailableSectors] = useState<any[]>([]);
  const [isChangingSector, setIsChangingSector] = useState(false);

  // Product selection modal state
  const [productSelectionModal, setProductSelectionModal] = useState<{
    open: boolean;
    mode: 'research' | 'wheel';
  }>({ open: false, mode: 'research' });

  // Persona-Benefit mapping state
  const [personaBenefits, setPersonaBenefits] = useState<Map<string, any[]>>(new Map());
  const [benefitMappingDialog, setBenefitMappingDialog] = useState<{
    open: boolean;
    mode: 'persona-to-benefits' | 'benefit-to-personas';
    personaName?: string;
    personaDescription?: string;
    benefitName?: string;
    benefitDescription?: string;
  }>({ open: false, mode: 'persona-to-benefits' });

  // AI Auto-mapping state
  const [autoMappingInProgress, setAutoMappingInProgress] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<{
    percentage: number;
    processed: number;
    total: number;
    successCount: number;
  } | null>(null);

  // Strategic insights refresh state
  const [refreshingInsights, setRefreshingInsights] = useState(false);

  // Benefit detail view state
  const [benefitDetailDialog, setBenefitDetailDialog] = useState<{
    open: boolean;
    personaName: string;
    personaDescription?: string;
    categoryTemplates: any[];
  }>({ open: false, personaName: '', categoryTemplates: [] });

  // Category detail panel state (for both leaf and non-leaf nodes)
  const [leafDetailPanel, setLeafDetailPanel] = useState<{
    open: boolean;
    category: {
      id: string;
      category_name: string;
      category_path: string;
      hasChildren?: boolean;
    } | null;
  }>({ open: false, category: null });

  // URL parameter handling for deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('categories');

  // Read URL parameters on mount for tab and category deep linking
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const openCategoryParam = searchParams.get('openCategory');

    // Set active tab from URL parameter (support aliases)
    if (tabParam) {
      // Map URL-friendly names to internal tab values
      const tabAliases: Record<string, string> = {
        'research': 'topics',        // /sector?tab=research -> topics tab
        'questions': 'wheels',       // /sector?tab=questions -> wheels tab
      };
      const mappedTab = tabAliases[tabParam] || tabParam;

      if (['categories', 'product-intelligence', 'trends', 'topics', 'wheels', 'segments', 'personas', 'insights'].includes(mappedTab)) {
        setActiveTab(mappedTab);
        // Scroll to top when navigating via URL parameter
        window.scrollTo(0, 0);
      }
    }

    // If openCategory param is present, open the category detail panel
    if (openCategoryParam) {
      // Open the category detail panel with the provided category name
      setLeafDetailPanel({
        open: true,
        category: {
          id: `category-${openCategoryParam}`,
          category_name: openCategoryParam,
          category_path: openCategoryParam,
          hasChildren: false
        }
      });

      // Clear URL params after reading
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openCategory');
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, []); // Run only once on mount

  useEffect(() => {
    if (selectedSite?.id) {
      loadSectorData();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (personas.length > 0 && sectorData?.id) {
      personas.forEach(persona => {
        if (persona.name) {
          loadPersonaBenefits(persona.name);
        }
      });
    }
  }, [personas, sectorData?.id]);

  useEffect(() => {
    if (selectedSite?.id) {
      loadAvailableSectors();
    }
  }, [selectedSite]);

  useEffect(() => {
    if (categoryViewMode === 'tree' && sectorData?.id && treeCategories.length === 0) {
      loadTreeViewData(sectorData.id);
    }
  }, [categoryViewMode, sectorData?.id]);

  // Sync category display language with global language preference
  useEffect(() => {
    // When global language changes, update category display language
    // 'en' and 'en-US' map to 'original', other languages map directly
    const newDisplayLang = (language === 'en' || language === 'en-US') ? 'original' : language;
    if (displayLanguage !== newDisplayLang) {
      setDisplayLanguage(newDisplayLang);
    }
  }, [language]);

  // Translate personas when language or data changes
  useEffect(() => {
    const translatePersonaData = async () => {
      if (language === 'en' || language === 'en-US' || personas.length === 0) {
        setTranslatedPersonas(personas);
        return;
      }

      setIsTranslatingPersonas(true);
      try {
        const translated = await Promise.all(
          personas.map(async (persona, idx) => {
            const fieldsToTranslate = [
              { field: 'description', value: persona.description },
              { field: 'buying_behavior', value: persona.buying_behavior },
              { field: 'expectations', value: persona.expectations }
            ].filter(f => f.value);

            const translations = await Promise.all(
              fieldsToTranslate.map(async ({ field, value }) => ({
                field,
                translated: (await translate(value, 'buyer_persona', `sector_persona_${idx}_${field}`)).translated
              }))
            );

            const translatedPersona = { ...persona };
            translations.forEach(({ field, translated: translatedValue }) => {
              translatedPersona[field] = translatedValue;
            });
            return translatedPersona;
          })
        );
        setTranslatedPersonas(translated);
      } catch (error) {
        console.error('Error translating personas:', error);
        setTranslatedPersonas(personas);
      } finally {
        setIsTranslatingPersonas(false);
      }
    };

    translatePersonaData();
  }, [language, personas, translate]);

  // Load available markets for Google Ads enrichment
  useEffect(() => {
    const loadMarkets = async () => {
      try {
        const response = await authFetch('/api/sectors/markets');
        if (response.ok) {
          const data = await response.json();
          setAvailableMarkets(data.markets || []);
        }
      } catch (error) {
        console.error('Error loading markets:', error);
      }
    };
    loadMarkets();
  }, []);

  // Load enriched markets when sector changes
  useEffect(() => {
    const loadEnrichedMarkets = async () => {
      if (!sectorData?.id) return;
      try {
        const response = await authFetch(`/api/sectors/${sectorData.id}/enriched-markets`);
        if (response.ok) {
          const data = await response.json();
          const markets = data.markets || [];
          setEnrichedMarkets(markets);
          // Set viewMarket to first enriched market if current selection isn't enriched
          if (markets.length > 0 && !markets.some((m: any) => m.market === viewMarket)) {
            setViewMarket(markets[0].market);
          }
        }
      } catch (error) {
        console.error('Error loading enriched markets:', error);
      }
    };
    loadEnrichedMarkets();
  }, [sectorData?.id]);

  // Load available languages when sector changes
  useEffect(() => {
    const loadAvailableLanguages = async () => {
      if (!sectorData?.id) return;
      try {
        const response = await authFetch(`/api/sectors/${sectorData.id}/taxonomy/translations`);
        if (response.ok) {
          const data = await response.json();
          setAvailableLanguages(data.available_languages || []);
        }
      } catch (error) {
        console.error('Error loading available languages:', error);
      }
    };
    loadAvailableLanguages();
  }, [sectorData?.id]);

  // Translate perceptions in the research modal when language changes or modal content changes
  useEffect(() => {
    const translatePerceptionsData = async () => {
      // If English or no perceptions, use original data
      if (language === 'en' || !researchModal.perceptions || researchModal.perceptions.length === 0) {
        setTranslatedPerceptions(researchModal.perceptions || []);
        return;
      }

      // Only translate when modal is open
      if (!researchModal.open) {
        return;
      }

      setIsTranslatingPerceptions(true);
      try {
        const translated = await Promise.all(
          researchModal.perceptions.map(async (attr: any, idx: number) => {
            const translations: any = { ...attr };

            // Translate attribute_name
            if (attr.attribute_name) {
              const nameResult = await translate(
                attr.attribute_name,
                'perceived_benefit',
                `perception_${idx}_name`
              );
              translations.attribute_name = nameResult.translated;
            }

            // Translate customer_description
            if (attr.customer_description) {
              const descResult = await translate(
                attr.customer_description,
                'perceived_benefit',
                `perception_${idx}_desc`
              );
              translations.customer_description = descResult.translated;
            }

            // Translate common phrases
            if (attr.common_phrases && attr.common_phrases.length > 0) {
              const translatedPhrases = await Promise.all(
                attr.common_phrases.map(async (phrase: string, pIdx: number) => {
                  const phraseResult = await translate(
                    phrase,
                    'perceived_benefit',
                    `perception_${idx}_phrase_${pIdx}`
                  );
                  return phraseResult.translated;
                })
              );
              translations.common_phrases = translatedPhrases;
            }

            return translations;
          })
        );
        setTranslatedPerceptions(translated);
      } catch (error) {
        console.error('Error translating perceptions:', error);
        setTranslatedPerceptions(researchModal.perceptions);
      } finally {
        setIsTranslatingPerceptions(false);
      }
    };

    translatePerceptionsData();
  }, [language, researchModal.open, researchModal.perceptions, translate]);

  // State for translation loading
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  // Load translations when display language changes (create on-demand if needed)
  useEffect(() => {
    const loadOrCreateTranslations = async () => {
      if (!sectorData?.id || displayLanguage === 'original') {
        setCategoryTranslations({});
        return;
      }

      setIsLoadingTranslations(true);
      console.log('[Translations] Loading translations for language:', displayLanguage);
      try {
        // First try to load existing translations
        const response = await authFetch(`/api/sectors/${sectorData.id}/taxonomy/translations?language=${displayLanguage}`);
        console.log('[Translations] GET response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('[Translations] GET data:', data);

          // Check if we have actual translations (not just empty rows from LEFT JOIN)
          // The getSectorTranslations returns rows even without translations (translated_name will be null)
          const allRows = data.translations || [];
          const actualTranslations = allRows.filter((t: any) => t.translated_name != null);
          const missingTranslations = allRows.filter((t: any) => t.translated_name == null);
          console.log('[Translations] Total rows:', allRows.length, 'Actual:', actualTranslations.length, 'Missing:', missingTranslations.length);

          // If there are missing translations OR no translations exist at all, create them on-demand
          // Note: allRows.length === 0 means no translations exist for this language yet
          const needsTranslation = missingTranslations.length > 0 || allRows.length === 0;
          if (needsTranslation) {
            console.log('[Translations] Missing translations found, creating on-demand...');
            const languageNames: Record<string, string> = {
              es: 'Spanish', de: 'German', fr: 'French', it: 'Italian'
            };
            const languageName = languageNames[displayLanguage] || displayLanguage;
            const translationCount = missingTranslations.length > 0 ? missingTranslations.length : 'all';

            // Show persistent loading toast
            const { dismiss } = toast({
              title: '🔄 Translating categories...',
              description: `Translating ${translationCount} category names to ${languageName}. This may take a moment...`,
              duration: 60000, // 60 seconds - long enough for the operation
            });

            // Create translations (the backend will only translate missing ones)
            const createResponse = await authFetch(`/api/sectors/${sectorData.id}/taxonomy/translations`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ language: displayLanguage })
            });

            // Dismiss the loading toast
            dismiss();

            console.log('[Translations] POST response status:', createResponse.status);
            if (createResponse.ok) {
              const createData = await createResponse.json();
              console.log('[Translations] POST data:', createData);
              const translationMap: Record<string, string> = {};
              (createData.translations || []).forEach((t: any) => {
                if (t.translated_name) {
                  translationMap[t.category_id] = t.translated_name;
                }
              });
              setCategoryTranslations(translationMap);
              toast({
                title: '✓ Translations ready',
                description: `${createData.translations_created} categories translated to ${languageName}`,
                duration: 5000,
              });
            } else {
              console.error('[Translations] POST failed:', await createResponse.text());
              toast({
                title: 'Translation failed',
                description: 'Could not create translations. Please try again.',
                variant: 'destructive',
              });
            }
          } else {
            console.log('[Translations] All translations exist, using them');
            // Use existing translations (only those with actual values)
            const translationMap: Record<string, string> = {};
            actualTranslations.forEach((t: any) => {
              translationMap[t.category_id] = t.translated_name;
            });
            setCategoryTranslations(translationMap);
          }
        }
      } catch (error) {
        console.error('Error loading translations:', error);
        toast({
          title: 'Translation error',
          description: 'Could not load translations',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingTranslations(false);
      }
    };
    loadOrCreateTranslations();
  }, [sectorData?.id, displayLanguage]);

  // Track if this is the initial load to prevent double-fetching
  const [initialMarketSet, setInitialMarketSet] = useState(false);

  // Reload taxonomy when view market changes (but not on initial load)
  useEffect(() => {
    if (!sectorData?.id || !initialMarketSet) return;
    // Reload with the selected market's data
    loadTaxonomyData(sectorData.id, currentTaxonomyView?.id || undefined, viewMarket);
  }, [viewMarket]);

  // Mark initial market as set after enriched markets are loaded
  useEffect(() => {
    if (enrichedMarkets.length > 0 && !initialMarketSet) {
      setInitialMarketSet(true);
    }
  }, [enrichedMarkets]);

  const loadSectorData = async (sectorId?: number) => {
    if (!selectedSite?.id) return;

    setLoading(true);
    try {
      // Load sector information - either specific sector or primary sector
      const endpoint = sectorId
        ? `/api/sectors/${sectorId}`
        : `/api/sites/${selectedSite.id}/sector`;

      const sectorResponse = await authFetch(endpoint);
      if (sectorResponse.ok) {
        const data = await sectorResponse.json();
        setSectorData(data.sector);
        setTrends(data.trends || []);
        setTopics(data.topics || []);
        setPersonas(data.personas || []);
        setSiteSegments(data.siteSegments || []);

        // Load taxonomy data, question wheels, approved research, and mappings if sector exists
        if (data.sector?.id) {
          loadTaxonomyData(data.sector.id);
          loadQuestionWheels(data.sector.id);
          loadApprovedResearch(data.sector.id);
          loadMappingsStats();
        }
      }
    } catch (error) {
      console.error('Error loading sector data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sector information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSectors = async () => {
    if (!selectedSite?.id) return;

    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/sectors`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSectors(data.sectors || []);
      }
    } catch (error) {
      console.error('Error loading site sectors:', error);
    }
  };

  const handleSectorChange = async (newSectorId: string) => {
    if (!selectedSite?.id || !newSectorId) return;

    setIsChangingSector(true);
    try {
      // Just reload data for the selected sector (no modification)
      await loadSectorData(parseInt(newSectorId));
    } catch (error) {
      console.error('Error switching sector view:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch sector view',
        variant: 'destructive'
      });
    } finally {
      setIsChangingSector(false);
    }
  };

  const loadApprovedResearch = async (sectorId: number) => {
    setLoadingResearch(true);
    try {
      const response = await authFetch(`${API_URL}/category-research/${sectorId}/approved`);
      if (response.ok) {
        const data = await response.json();
        setApprovedResearch(data.research || []);
      }
    } catch (error) {
      console.error('Error loading approved research:', error);
      toast({
        title: 'Error',
        description: 'Failed to load product intelligence research',
        variant: 'destructive',
      });
    } finally {
      setLoadingResearch(false);
    }
  };

  const loadTaxonomyData = async (sectorId: number, parentId?: number, market?: string) => {
    try {
      const params = new URLSearchParams();
      if (parentId) params.append('parentId', String(parentId));
      if (market) params.append('market', market);

      const url = `${API_URL}/sectors/${sectorId}/taxonomy${params.toString() ? '?' + params.toString() : ''}`;

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        const taxonomy = data.taxonomy || [];

        // Auto-drill down if only 1 root category exists AND it has children
        if (!parentId && taxonomy.length === 1) {
          // Check if the single root category has children before drilling down
          const childParams = new URLSearchParams();
          childParams.append('parentId', String(taxonomy[0].id));
          if (market) childParams.append('market', market);
          const childUrl = `${API_URL}/sectors/${sectorId}/taxonomy?${childParams.toString()}`;
          const childResponse = await authFetch(childUrl);
          if (childResponse.ok) {
            const childData = await childResponse.json();
            const children = childData.taxonomy || [];
            if (children.length > 0) {
              // Has children - drill down to show them
              setCurrentTaxonomyView(taxonomy[0]);
              setTaxonomyData(children);
            } else {
              // No children - show the root category itself
              setTaxonomyData(taxonomy);
            }
          } else {
            // Failed to load children - show root
            setTaxonomyData(taxonomy);
          }
        } else {
          setTaxonomyData(taxonomy);
        }
      }
    } catch (error) {
      console.error('Error loading taxonomy:', error);
    }
  };

  const loadTreeViewData = async (sectorId: number) => {
    setLoadingTreeView(true);
    try {
      // Query from v_category_tree view for hierarchical data
      const response = await authFetch(`${API_URL}/sectors/${sectorId}/taxonomy?tree=true`);
      if (response.ok) {
        const data = await response.json();
        const tree = data.taxonomy || [];

        // API already returns tree structure with nested children, no need to rebuild
        setTreeCategories(tree);

        // Auto-expand root node and its first level children for better UX
        if (tree.length > 0) {
          const nodesToExpand = new Set<string>();

          // Expand root node(s)
          tree.forEach((rootNode: any) => {
            nodesToExpand.add(rootNode.id);

            // Also expand first level children so users see the structure immediately
            if (rootNode.children && rootNode.children.length > 0) {
              rootNode.children.forEach((child: any) => {
                nodesToExpand.add(child.id);
              });
            }
          });

          setExpandedNodes(nodesToExpand);
        }
      }
    } catch (error) {
      console.error('Error loading tree view:', error);
      toast({
        title: 'Error',
        description: 'Failed to load category tree',
        variant: 'destructive',
      });
    } finally {
      setLoadingTreeView(false);
    }
  };

  const toggleNode = async (nodeId: string, node: any) => {
    const newExpanded = new Set(expandedNodes);

    if (newExpanded.has(nodeId)) {
      // Collapsing node
      newExpanded.delete(nodeId);
      setExpandedNodes(newExpanded);
      return;
    }

    // Expanding node
    newExpanded.add(nodeId);
    setExpandedNodes(newExpanded);

    // Check if lazy loading is needed (using google_product_category_id for Google taxonomy)
    const needsLazyLoad = node.google_product_category_id &&
                          node.level_depth < 3 &&  // Limit depth to prevent over-fetching
                          (!node.children || node.children.length === 0) &&
                          !loadingNodes.has(nodeId);

    if (needsLazyLoad && sectorData?.id) {
      // Mark as loading
      const newLoading = new Set(loadingNodes);
      newLoading.add(nodeId);
      setLoadingNodes(newLoading);

      try {
        const response = await authFetch(
          `${API_URL}/google-taxonomy/sectors/${sectorData.id}/categories/${nodeId}/expand`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (response.ok) {
          const data = await response.json();

          if (data.success && data.data.children.length > 0) {
            // Update tree with new children
            const updateNodeChildren = (nodes: any[]): any[] => {
              return nodes.map(n => {
                if (n.id === nodeId) {
                  return { ...n, children: data.data.children };
                } else if (n.children && n.children.length > 0) {
                  return { ...n, children: updateNodeChildren(n.children) };
                }
                return n;
              });
            };

            setTreeCategories(updateNodeChildren(treeCategories));

            if (!data.data.alreadyExists) {
              toast({
                title: 'Categories Loaded',
                description: `Added ${data.data.inserted} subcategories from Google Taxonomy`,
              });
            }
          }
        }
      } catch (error) {
        console.error('Error lazy-loading children:', error);
        toast({
          title: 'Error',
          description: 'Failed to load subcategories',
          variant: 'destructive',
        });
      } finally {
        // Remove from loading
        const newLoading = new Set(loadingNodes);
        newLoading.delete(nodeId);
        setLoadingNodes(newLoading);
      }
    }
  };

  const handleTogglePrimary = async (categoryId: string, isPrimary: boolean) => {
    if (!sectorData?.id) return;

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/categories/${categoryId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_primary_level: isPrimary }),
        }
      );

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Category ${isPrimary ? 'marked as primary' : 'unmarked as primary'}`,
        });
        // Reload tree view
        loadTreeViewData(sectorData.id);
      } else {
        throw new Error('Failed to update category');
      }
    } catch (error) {
      console.error('Error toggling primary status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update category',
        variant: 'destructive',
      });
    }
  };

  const handleSchemaOrgUpdate = async (categoryId: string, schemaOrgType: string) => {
    if (!sectorData?.id) return;

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/categories/${categoryId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            schema_org_type: schemaOrgType,
            schema_org_url: schemaOrgType ? `https://schema.org/${schemaOrgType}` : null,
          }),
        }
      );

      if (response.ok) {
        toast({
          title: 'Success',
          description: schemaOrgType
            ? `Schema.org type set to: ${schemaOrgType}`
            : 'Schema.org type cleared',
        });
        // Reload tree view
        loadTreeViewData(sectorData.id);
      } else {
        throw new Error('Failed to update category');
      }
    } catch (error) {
      console.error('Error updating Schema.org type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update Schema.org type',
        variant: 'destructive',
      });
    }
  };

  // Handle category click (for both leaf and non-leaf nodes)
  const handleLeafClick = (category: any) => {
    setLeafDetailPanel({
      open: true,
      category: {
        id: category.id,
        category_name: category.category_name,
        category_path: category.category_path,
        hasChildren: !!(category.children && category.children.length > 0),
      },
    });
  };

  // Handle navigation from leaf detail panel to tabs
  const handleNavigateToTab = (tab: string, data?: any) => {
    // Close the panel
    setLeafDetailPanel({ open: false, category: null });

    // Navigate to the appropriate tab
    // This will be handled by the Tabs component's value
    const tabsElement = document.querySelector('[role="tablist"]');
    if (tabsElement) {
      const targetTab = tabsElement.querySelector(`[data-value="${tab}"]`) as HTMLElement;
      if (targetTab) {
        targetTab.click();
      }
    }

    // If additional data is provided, handle specific filtering/navigation
    if (data) {
      if (data.categoryName && tab === 'product-intelligence') {
        // Find and click on the research card for this category
        setTimeout(() => {
          const categoryName = data.categoryName;
          const researchCards = document.querySelectorAll('[data-category-name]');
          researchCards.forEach(card => {
            if (card.getAttribute('data-category-name') === categoryName) {
              (card as HTMLElement).click();
            }
          });
        }, 300);
      } else if (data.personaName && tab === 'personas') {
        // Scroll to specific persona
        setTimeout(() => {
          const personaElement = document.getElementById(`persona-${data.personaName.replace(/\s+/g, '-')}`);
          if (personaElement) {
            personaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  };

  const loadQuestionWheels = async (sectorId: number) => {
    setLoadingWheels(true);
    try {
      const response = await authFetch(`${API_URL}/sectors/${sectorId}/question-wheels`);
      if (response.ok) {
        const data = await response.json();
        setQuestionWheels(data.wheels || []);
      }
    } catch (error) {
      console.error('Error loading question wheels:', error);
      toast({
        title: 'Error',
        description: 'Failed to load question wheels',
        variant: 'destructive',
      });
    } finally {
      setLoadingWheels(false);
    }
  };

  const loadPersonaBenefits = async (personaName: string) => {
    if (!sectorData?.id) return;

    try {
      const response = await authFetch(
        `${API_URL}/personas/${encodeURIComponent(personaName)}/category-templates?sectorId=${sectorData.id}`
      );
      const data = await response.json();
      if (data.success) {
        // Store the category-grouped data
        setPersonaBenefits(prev => new Map(prev).set(personaName, data.data.category_templates || []));
      }
    } catch (error) {
      console.error('Error loading persona benefits:', error);
    }
  };

  // Poll for job status
  const pollJobStatus = async (jobId: string, sectorId: number) => {
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorId}/mapping-jobs/${jobId}`
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch job status');
      }

      const job = data.data;

      // Update progress
      if (job.progress) {
        setJobProgress({
          percentage: job.progress.percentage || 0,
          processed: job.progress.processed || 0,
          total: job.progress.total || 0,
          successCount: job.results?.success_count || 0,
        });
      }

      // Check if job is complete
      if (job.status === 'completed') {
        setAutoMappingInProgress(false);
        setCurrentJobId(null);
        setJobProgress(null);

        toast({
          title: 'Success!',
          description: `AI generated ${job.results.success_count} persona-benefit mappings. ${job.results.error_count > 0 ? `(${job.results.error_count} errors)` : ''}`,
        });

        // Reload persona benefits to show the new mappings
        personas.forEach(persona => {
          if (persona.name) {
            loadPersonaBenefits(persona.name);
          }
        });

        return true; // Stop polling
      } else if (job.status === 'failed') {
        setAutoMappingInProgress(false);
        setCurrentJobId(null);
        setJobProgress(null);

        toast({
          title: 'Error',
          description: job.error_message || 'AI mapping job failed',
          variant: 'destructive',
        });

        return true; // Stop polling
      }

      return false; // Continue polling
    } catch (error: any) {
      console.error('Error polling job status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to check job status',
        variant: 'destructive',
      });
      setAutoMappingInProgress(false);
      setCurrentJobId(null);
      setJobProgress(null);
      return true; // Stop polling on error
    }
  };

  // Start AI auto-mapping job
  const handleAIAutoMap = async () => {
    if (!sectorData?.id) {
      toast({
        title: 'Error',
        description: 'Sector data not loaded',
        variant: 'destructive',
      });
      return;
    }

    setAutoMappingInProgress(true);
    setJobProgress(null);

    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/auto-generate-persona-benefit-mappings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to start AI mapping');
      }

      const jobId = data.data.job_id;
      setCurrentJobId(jobId);

      toast({
        title: 'AI Mapping Started',
        description: 'AI is analyzing personas and benefits. This may take several minutes.',
      });

      // Start polling for job status
      const pollInterval = setInterval(async () => {
        const shouldStop = await pollJobStatus(jobId, sectorData.id!);
        if (shouldStop) {
          clearInterval(pollInterval);
        }
      }, 3000); // Poll every 3 seconds

      // Cleanup on unmount
      return () => clearInterval(pollInterval);
    } catch (error: any) {
      console.error('Error starting AI mapping:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start AI mappings',
        variant: 'destructive',
      });
      setAutoMappingInProgress(false);
      setCurrentJobId(null);
    }
  };

  const handleEnrichTaxonomy = async () => {
    console.log('[handleEnrichTaxonomy] Called, sectorData:', sectorData);
    if (!sectorData?.id) {
      console.error('[handleEnrichTaxonomy] No sectorData.id, returning');
      return;
    }

    // Confirmation dialog with context-aware message
    const isInitializing = taxonomyData.length === 0;
    const confirmMessage = isInitializing
      ? `Initialize taxonomy from Google Product Taxonomy?\n\nThis will load the standard Google product categories for this sector. This is the recommended starting point for new sectors.`
      : `Generate AI-powered sub-categories for "${currentTaxonomyView?.category_name || sectorData.name}"?\n\n` +
        `This will:\n` +
        `- Create new child categories using AI analysis\n` +
        `- Add demand metrics to new categories\n` +
        `- May affect product mappings for sites using this sector\n\n` +
        `This operation cannot be easily undone. Continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsEnrichingTaxonomy(true);
    try {
      // If taxonomy is empty, initialize from Google Product Taxonomy
      // Otherwise, enrich existing categories
      const endpoint = isInitializing
        ? `${API_URL}/sectors/${sectorData.id}/init-from-google-taxonomy`
        : `${API_URL}/sectors/${sectorData.id}/enrich-taxonomy`;

      const body = taxonomyData.length === 0
        ? {} // No body for Google init
        : { parentCategoryId: currentTaxonomyView?.id || null }; // Parent ID for enrichment

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();

        if (taxonomyData.length === 0) {
          // Google taxonomy initialization
          toast({
            title: 'Taxonomy Initialized!',
            description: `Loaded ${result.totalCategories} categories from Google Product Taxonomy, organized into ${result.topLevelCategories} semantic groups`,
          });
        } else {
          // OpenAI enrichment
          toast({
            title: 'Success!',
            description: `Generated ${result.categories_generated} enriched categories with demand metrics`,
          });
        }

        // Reload taxonomy
        loadTaxonomyData(sectorData.id, currentTaxonomyView?.id);
      }
    } catch (error) {
      console.error('Error enriching taxonomy:', error);
      toast({
        title: 'Error',
        description: 'Failed to enrich product categories',
        variant: 'destructive',
      });
    } finally {
      setIsEnrichingTaxonomy(false);
    }
  };

  // Enrich existing categories with Google Ads search volume and CPC data
  const handleEnrichWithGoogleAds = async () => {
    if (!sectorData?.id) return;
    if (taxonomyData.length === 0) {
      toast({
        title: 'No Categories',
        description: 'Initialize the taxonomy first before enriching with Google Ads data',
        variant: 'destructive',
      });
      return;
    }

    const categoryCount = taxonomyData.length;
    const categoryContext = currentTaxonomyView?.category_name || sectorData.name;

    const confirmMessage =
      `Enrich ${categoryCount} categories under "${categoryContext}" with Google Ads data for ${selectedMarket}?\n\n` +
      `This will:\n` +
      `- Fetch search volume from Google Ads API (${selectedMarket})\n` +
      `- Add CPC (cost per click) data\n` +
      `- Calculate competition levels\n` +
      `- Update demand scores\n\n` +
      `This uses Google Ads API quota. Continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsEnrichingWithGoogleAds(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/enrich-taxonomy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentCategoryId: currentTaxonomyView?.id || null,
            useImported: true, // This triggers Google Ads enrichment
            market: selectedMarket
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        const translationInfo = result.translations_created > 0
          ? ` (${result.translations_created} translations created)`
          : '';
        toast({
          title: 'Google Ads Enrichment Complete!',
          description: `Enriched ${result.categories_enriched || categoryCount} categories with search volume and CPC data for ${selectedMarket}${translationInfo}`,
        });
        // Reload taxonomy to show updated metrics
        loadTaxonomyData(sectorData.id, currentTaxonomyView?.id);
        // Reload enriched markets list
        const marketsResponse = await authFetch(`/api/sectors/${sectorData.id}/enriched-markets`);
        if (marketsResponse.ok) {
          const marketsData = await marketsResponse.json();
          setEnrichedMarkets(marketsData.markets || []);
        }
      } else {
        const error = await response.json();
        throw new Error(error.details || 'Failed to enrich with Google Ads data');
      }
    } catch (error: any) {
      console.error('Error enriching with Google Ads:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to enrich categories with Google Ads data',
        variant: 'destructive',
      });
    } finally {
      setIsEnrichingWithGoogleAds(false);
    }
  };

  // Generate categories from imported products
  const handleGenerateFromProducts = async () => {
    if (!sectorData?.id) return;

    const confirmMessage =
      `Generate category templates from imported products?\n\n` +
      `This will:\n` +
      `- Scan all products in sites linked to this sector\n` +
      `- Extract unique category paths from product data\n` +
      `- Create hierarchical category templates automatically\n\n` +
      `Existing categories will be preserved. Continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsGeneratingFromProducts(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/generate-categories-from-products`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Categories Generated!',
          description: `Created ${result.insertedCount} categories from ${result.sitesProcessed} site(s). ${result.skippedCount} already existed.`,
        });
        // Reload taxonomy to show new categories
        loadTaxonomyData(sectorData.id, currentTaxonomyView?.id);
      } else {
        const error = await response.json();
        throw new Error(error.message || error.details || 'Failed to generate categories');
      }
    } catch (error: any) {
      console.error('Error generating categories from products:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate categories from products',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingFromProducts(false);
    }
  };

  const handleReorganizeTaxonomy = async () => {
    console.log('[handleReorganizeTaxonomy] Called, sectorData:', sectorData);
    if (!sectorData?.id) {
      console.error('[handleReorganizeTaxonomy] No sectorData.id, returning');
      return;
    }

    setIsReorganizingTaxonomy(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/reorganize-taxonomy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentCategoryId: currentTaxonomyView?.id || null
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Reorganization Complete!',
          description: `Created ${result.clusters_created} parent categories and reorganized ${result.categories_reorganized} items`,
        });
        // Reload taxonomy
        loadTaxonomyData(sectorData.id, currentTaxonomyView?.id);
      }
    } catch (error) {
      console.error('Error reorganizing taxonomy:', error);
      toast({
        title: 'Error',
        description: 'Failed to reorganize taxonomy',
        variant: 'destructive',
      });
    } finally {
      setIsReorganizingTaxonomy(false);
    }
  };

  const handleResetTaxonomy = async () => {
    console.log('[handleResetTaxonomy] Called, sectorData:', sectorData);
    if (!sectorData?.id) {
      console.error('[handleResetTaxonomy] No sectorData.id, returning');
      return;
    }

    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete ALL taxonomy data and start fresh? This cannot be undone.')) {
      console.log('[handleResetTaxonomy] User cancelled');
      return;
    }

    setIsResettingTaxonomy(true);
    try {
      const response = await authFetch(
        `${API_URL}/sectors/${sectorData.id}/taxonomy`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Taxonomy Reset!',
          description: `Deleted ${result.categories_deleted} categories. You can now enrich from scratch.`,
        });

        // Reset state
        setTaxonomyData([]);
        setTaxonomyBreadcrumb([]);
        setCurrentTaxonomyView(null);
      }
    } catch (error) {
      console.error('Error resetting taxonomy:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset taxonomy',
        variant: 'destructive',
      });
    } finally {
      setIsResettingTaxonomy(false);
    }
  };

  const handleCategoryClick = async (category: any) => {
    if (!sectorData?.id) return;

    // First, try to load children to see if this is a leaf node
    const url = `${API_URL}/sectors/${sectorData.id}/taxonomy?parentId=${category.id}`;
    try {
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        const children = data.taxonomy || [];

        // If no children, this is a leaf node - open detail panel
        if (children.length === 0) {
          handleLeafClick({
            id: category.id,
            category_name: category.category_name,
            category_path: category.category_path || category.name,
            hasChildren: false,
          });
          return;
        }

        // Has children - navigate into category
        setTaxonomyBreadcrumb([...taxonomyBreadcrumb, category]);
        setCurrentTaxonomyView(category);
        setTaxonomyData(children);
      }
    } catch (error) {
      console.error('Error checking category children:', error);
      // Fallback to old behavior
      setTaxonomyBreadcrumb([...taxonomyBreadcrumb, category]);
      setCurrentTaxonomyView(category);
      loadTaxonomyData(sectorData.id, category.id);
    }
  };

  const handleTaxonomyBreadcrumbClick = (index: number) => {
    if (!sectorData?.id) return;

    // Get the category at the clicked index
    const targetCategory = taxonomyBreadcrumb[index];

    // Update breadcrumb to include only items up to and including the clicked item
    setTaxonomyBreadcrumb(taxonomyBreadcrumb.slice(0, index + 1));
    setCurrentTaxonomyView(targetCategory);

    // Load children of the clicked category
    loadTaxonomyData(sectorData.id, targetCategory.id);
  };

  const refreshTrends = async () => {
    if (!selectedSite?.id || !sectorData?.id) return;
    
    setRefreshingTrends(true);
    try {
      const response = await authFetch(`/api/sectors/${sectorData.id}/refresh-trends`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrends(data.trends);
        toast({
          title: 'Success',
          description: 'Sector trends have been updated',
        });
      }
    } catch (error) {
      console.error('Error refreshing trends:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh trends',
        variant: 'destructive',
      });
    } finally {
      setRefreshingTrends(false);
    }
  };

  const generateSectorImage = async () => {
    if (!sectorData?.id) return;

    setGeneratingImage(true);
    try {
      const response = await authFetch(`/api/sectors/${sectorData.id}/generate-image`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        setSectorData(prev => prev ? { ...prev, image_url: data.image_url } : null);
        toast({
          title: 'Success',
          description: 'Sector image has been generated',
        });
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate sector image',
        variant: 'destructive',
      });
    } finally {
      setGeneratingImage(false);
    }
  };

  const loadMappingsStats = async () => {
    if (!sectorData?.id) return;

    setLoadingMappings(true);
    try {
      const [benefitsRes, mappingsRes, rangesRes] = await Promise.all([
        authFetch(`${API_URL}/mappings/benefits/${sectorData.id}`),
        authFetch(`${API_URL}/mappings/benefit-attributes/${sectorData.id}`),
        authFetch(`${API_URL}/mappings/value-ranges/${sectorData.id}`)
      ]);

      if (benefitsRes.ok && mappingsRes.ok && rangesRes.ok) {
        const [benefitsData, mappingsData, rangesData] = await Promise.all([
          benefitsRes.json(),
          mappingsRes.json(),
          rangesRes.json()
        ]);

        setMappingsStats({
          benefits: benefitsData.count || 0,
          mappings: mappingsData.count || 0,
          valueRanges: rangesData.count || 0
        });
      }
    } catch (error) {
      console.error('Error loading mappings stats:', error);
    } finally {
      setLoadingMappings(false);
    }
  };

  const extendMappingCoverage = async () => {
    if (!sectorData?.id) return;

    setExtendingCoverage(true);
    try {
      const response = await authFetch(`${API_URL}/mappings/populate/${sectorData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        // Reports what was actually written — a zero must never render as a success toast.
        toast({
          title: data.wroteNothing ? 'Nothing was written' : 'Mapping coverage extended',
          description: data.message || `Extended mapping coverage for ${sectorData.name} sector`,
          ...(data.success ? {} : { variant: 'destructive' as const }),
        });
        // Reload stats
        await loadMappingsStats();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to extend mapping coverage',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error extending coverage:', error);
      toast({
        title: 'Error',
        description: 'Failed to extend mapping coverage',
        variant: 'destructive',
      });
    } finally {
      setExtendingCoverage(false);
    }
  };

  const regeneratePersonaImage = async (personaId: string, personaName: string) => {
    setRegeneratingPersonas(prev => new Set(prev).add(personaId));

    try {
      const response = await authFetch(`/api/personas/${personaId}/generate-image`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        // Update the persona in the local state
        setPersonas(prev => prev.map(p =>
          p.id === personaId ? { ...p, image_url: data.image_url } : p
        ));
        toast({
          title: 'Success',
          description: `Image for ${personaName} has been regenerated`,
        });
      } else {
        throw new Error('Failed to regenerate image');
      }
    } catch (error) {
      console.error('Error regenerating persona image:', error);
      toast({
        title: 'Error',
        description: `Failed to regenerate image for ${personaName}`,
        variant: 'destructive',
      });
    } finally {
      setRegeneratingPersonas(prev => {
        const newSet = new Set(prev);
        newSet.delete(personaId);
        return newSet;
      });
    }
  };

  const fetchTopicResearch = async (topicId: string, topicTitle: string) => {
    setTopicResearchModal({
      open: true,
      title: topicTitle,
      content: '',
      loading: true
    });

    try {
      const response = await authFetch(`/api/topics/${topicId}/research`);

      if (response.ok) {
        const data = await response.json();
        // Extract the research_content field from the research object
        const researchContent = typeof data.research === 'object'
          ? (data.research.research_content || data.research.content || 'No research content available.')
          : (data.research || 'No research content available.');

        setTopicResearchModal(prev => ({
          ...prev,
          loading: false,
          content: researchContent
        }));
      } else {
        throw new Error('Failed to fetch research');
      }
    } catch (error) {
      setTopicResearchModal(prev => ({
        ...prev,
        loading: false,
        content: 'Failed to load research content. Please try again.'
      }));
      toast({
        title: 'Error',
        description: 'Failed to fetch research content',
        variant: 'destructive',
      });
    }
  };

  const viewPerceptionResearch = async (categoryName: string) => {
    if (!sectorData?.id) return;

    setResearchModal({
      open: true,
      title: `${categoryName} - Customer Perceptions`,
      perceptions: [],
      loading: true
    });

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorData.id}/perceptions/${encodeURIComponent(categoryName)}`
      );

      if (response.ok) {
        const data = await response.json();
        const perceptions = data.perceptions || [];

        setResearchModal(prev => ({
          ...prev,
          loading: false,
          perceptions
        }));
      } else {
        throw new Error('Failed to fetch perception research');
      }
    } catch (error) {
      setResearchModal(prev => ({
        ...prev,
        loading: false,
        perceptions: [] // Ensure perceptions is always an array
      }));
      toast({
        title: 'Error',
        description: 'Failed to fetch perception research',
        variant: 'destructive',
      });
    }
  };

  const generateQuestionsFromResearch = async (categoryName: string, forceRegenerate = false) => {
    if (!sectorData?.id) return;

    setGeneratingQuestions(categoryName);

    // Show immediate feedback
    toast({
      title: forceRegenerate ? 'Regenerating Questions' : 'Loading Questions',
      description: forceRegenerate
        ? `AI is generating fresh questions for ${categoryName}. This may take 10-30 seconds...`
        : `Loading questions for ${categoryName}. Checking cache...`,
    });

    setQuestionsModal({
      open: true,
      title: `${categoryName} - Generated Questions`,
      questions: [],
      loading: true,
      cached: false,
      categoryName,
    });

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${sectorData.id}/generate-questions/${encodeURIComponent(categoryName)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questionsPerAttribute: 3,
            language: 'en',
            forceRegenerate,
          })
        }
      );

      if (response.ok) {
        const data = await response.json();

        setQuestionsModal(prev => ({
          ...prev,
          loading: false,
          questions: data.questions || [],
          cached: data.cached || false,
        }));

        if (data.cached) {
          toast({
            title: 'Loaded from Cache',
            description: `Using ${data.totalQuestions || 0} previously generated questions`,
          });
        } else {
          toast({
            title: 'Success',
            description: `Generated ${data.totalQuestions || 0} questions from ${data.perceptionsUsed || 0} perceived attributes`,
          });
        }
      } else {
        throw new Error('Failed to generate questions');
      }
    } catch (error) {
      setQuestionsModal(prev => ({
        ...prev,
        loading: false
      }));
      toast({
        title: 'Error',
        description: 'Failed to generate questions. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGeneratingQuestions(null);
    }
  };

  // Helper functions for attribute display
  function getAttributeTypeBadge(type: string) {
    const colors: Record<string, string> = {
      // Functional layer
      performance: 'bg-blue-100 text-blue-800',
      convenience: 'bg-cyan-100 text-cyan-800',
      protection: 'bg-indigo-100 text-indigo-800',
      adaptability: 'bg-sky-100 text-sky-800',
      innovation: 'bg-violet-100 text-violet-800',
      // Emotional layer
      freedom: 'bg-rose-100 text-rose-800',
      confidence: 'bg-pink-100 text-pink-800',
      joy: 'bg-red-100 text-red-800',
      pride: 'bg-orange-100 text-orange-800',
      achievement: 'bg-amber-100 text-amber-800',
      // Aspirational layer
      health: 'bg-green-100 text-green-800',
      sustainability: 'bg-emerald-100 text-emerald-800',
      modernity: 'bg-teal-100 text-teal-800',
      balance: 'bg-lime-100 text-lime-800',
      belonging: 'bg-mint-100 text-mint-800',
      // Legacy types
      functional: 'bg-blue-100 text-blue-800',
      use_case: 'bg-green-100 text-green-800',
      maintenance: 'bg-orange-100 text-orange-800',
      trust: 'bg-purple-100 text-purple-800',
      value: 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }

  function getSentimentIcon(sentiment: string) {
    switch (sentiment) {
      case 'positive':
        return <ThumbsUp className="h-3 w-3" />;
      case 'negative':
        return <ThumbsDown className="h-3 w-3" />;
      case 'question':
        return <HelpCircle className="h-3 w-3" />;
      default:
        return <MessageSquare className="h-3 w-3" />;
    }
  }

  function getSentimentBadgeClass(sentiment: string) {
    switch (sentiment) {
      case 'positive':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'negative':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'question':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  }

  const handleTriggerResearch = async (params: { productIds: string[]; maxProducts: number; language: string }) => {
    if (!sectorData?.id) return;

    try {
      const response = await authFetch(`${API_URL}/sectors/${sectorData.id}/trigger-research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Research Started',
          description: `Research topic identification has been queued. Job ID: ${result.jobId}`,
        });
      } else {
        throw new Error('Failed to start research');
      }
    } catch (error) {
      console.error('Error triggering research:', error);
      toast({
        title: 'Error',
        description: 'Failed to start research topic identification',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // View research document for a topic
  const handleViewResearch = async (topicId: string) => {
    try {
      setLoadingResearchContent(true);
      setResearchDialog({ open: true, topicId, research: null });

      const response = await authFetch(`/api/topics/${topicId}/research`);

      if (!response.ok) {
        throw new Error('Failed to fetch research');
      }

      const data = await response.json();
      setResearchDialog({ open: true, topicId, research: data.research });
    } catch (error) {
      console.error('Error fetching research:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research document. Research may not exist for this topic.',
        variant: 'destructive',
      });
      setResearchDialog({ open: false, topicId: null, research: null });
    } finally {
      setLoadingResearchContent(false);
    }
  };

  const handleGenerateWheel = async (params: { productIds: string[]; maxProducts: number; language: string }) => {
    if (!sectorData?.id) return;

    try {
      const response = await authFetch(`${API_URL}/sectors/${sectorData.id}/generate-wheel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Wheel Generation Started',
          description: `Question wheel generation has been queued. Job ID: ${result.jobId}`,
        });
        // Reload question wheels after a delay
        setTimeout(() => {
          if (sectorData?.id) loadQuestionWheels(sectorData.id);
        }, 3000);
      } else {
        throw new Error('Failed to start wheel generation');
      }
    } catch (error) {
      console.error('Error generating wheel:', error);
      toast({
        title: 'Error',
        description: 'Failed to start question wheel generation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Load existing trend research when opening trend detail modal
  const loadTrendResearch = async (trendId: string) => {
    try {
      setLoadingTrendResearch(true);
      setTrendResearch(null);

      const response = await authFetch(`${API_URL}/trends/${trendId}/research?language=${language || 'en'}`);

      if (response.ok) {
        const data = await response.json();
        if (data.research && data.hasResearch) {
          // Map API response to TrendResearch interface
          setTrendResearch({
            id: data.research.id,
            trend_id: data.research.trendId,
            research_content: data.research.content || '',
            research_summary: data.research.summary || '',
            key_findings: data.research.keyFindings || [],
            sources: data.research.sources || [],
            citationSources: data.research.citationSources || [],
            research_quality_score: data.research.qualityScore || 0,
            word_count: data.research.wordCount || 0,
            research_depth: data.research.depth || 'standard',
            display_language: data.research.language || 'en',
            created_at: data.research.createdAt || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading trend research:', error);
      // Silently fail - research may not exist yet
    } finally {
      setLoadingTrendResearch(false);
    }
  };

  // Generate deep research for a trend (with async job support)
  const generateTrendResearch = async (trendId: string, depth: 'quick' | 'standard' | 'comprehensive' = 'standard') => {
    try {
      setGeneratingTrendResearch(depth);

      // Use async mode to avoid Cloudflare timeout
      const response = await authFetch(`${API_URL}/trends/${trendId}/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          depth,
          targetLanguage: language || 'en',
          async: true // Enable async mode
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start research generation');
      }

      const jobResult = await response.json();

      // Handle async job response
      if (jobResult.async && jobResult.jobId) {
        console.log(`[TrendResearch] Started async job ${jobResult.jobId}`);

        // Poll for job completion
        const pollInterval = setInterval(async () => {
          try {
            const jobStatusResponse = await authFetch(`${API_URL}/jobs/${jobResult.jobId}`);
            if (!jobStatusResponse.ok) {
              throw new Error('Failed to check job status');
            }

            const jobStatus = await jobStatusResponse.json();
            console.log(`[TrendResearch] Job status: ${jobStatus.status}, progress: ${jobStatus.progress?.percent || 0}%`);

            if (jobStatus.status === 'completed') {
              clearInterval(pollInterval);

              // Fetch the generated research
              const researchResponse = await authFetch(`${API_URL}/trends/${trendId}/research?language=${language || 'en'}`);
              if (researchResponse.ok) {
                const researchData = await researchResponse.json();
                if (researchData.success && researchData.research) {
                  const research = researchData.research;
                  setTrendResearch({
                    id: research.id,
                    trend_id: trendId,
                    research_content: research.content || '',
                    research_summary: research.summary || '',
                    key_findings: research.key_findings || research.keyFindings || [],
                    sources: research.sources || [],
                    citationSources: research.citationSources || [],
                    research_quality_score: research.research_quality_score || research.qualityScore || 0,
                    word_count: research.word_count || research.wordCount || 0,
                    research_depth: depth,
                    display_language: research.display_language || language || 'en',
                    created_at: research.created_at || new Date().toISOString()
                  });

                  // Update trend to show it has research
                  setTrends(prev => prev.map(t =>
                    t.id === trendId ? { ...t, has_research: true } : t
                  ));

                  toast({
                    title: 'Research Generated',
                    description: `Deep research generated with ${research.word_count || research.wordCount || 0} words.`,
                  });
                }
              }
              setGeneratingTrendResearch(null);
            } else if (jobStatus.status === 'failed') {
              clearInterval(pollInterval);
              throw new Error(jobStatus.error_message || 'Research generation failed');
            }
          } catch (pollError: any) {
            clearInterval(pollInterval);
            console.error('Error polling job status:', pollError);
            toast({
              title: 'Error',
              description: pollError.message || 'Failed to check generation progress. Please try again.',
              variant: 'destructive',
            });
            setGeneratingTrendResearch(null);
          }
        }, 3000); // Poll every 3 seconds

        return; // Don't clear generating state yet - will be cleared when job completes
      }

      // Handle synchronous response (fallback)
      if (jobResult.success && jobResult.research) {
        const research = jobResult.research;
        setTrendResearch({
          id: research.id,
          trend_id: trendId,
          research_content: research.content || '',
          research_summary: research.summary || '',
          key_findings: research.keyFindings || [],
          sources: research.sources || [],
          citationSources: research.citationSources || [],
          research_quality_score: research.qualityScore || 0,
          word_count: research.wordCount || 0,
          research_depth: depth,
          display_language: research.targetLanguage || 'en',
          created_at: new Date().toISOString()
        });

        setTrends(prev => prev.map(t =>
          t.id === trendId ? { ...t, has_research: true } : t
        ));

        toast({
          title: 'Research Generated',
          description: `Deep research generated with ${research.wordCount || 0} words.`,
        });
        setGeneratingTrendResearch(null);
      }
    } catch (error: any) {
      console.error('Error generating trend research:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate research. Please try again.',
        variant: 'destructive',
      });
      setGeneratingTrendResearch(null);
    }
  };

  // Handle opening trend detail modal
  const handleOpenTrendDetail = (trend: SectorTrend) => {
    setTrendDetailModal({ open: true, trend });
    setTrendResearch(null);
    // Load existing research if available
    loadTrendResearch(trend.id);
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view sector information.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!sectorData) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No sector information available for this site. The sector analysis may still be in progress.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold">The Sector</h1>
            <p className="text-muted-foreground mt-2">
              Deep insights into the {sectorData.name} sector for {selectedSite.name}
            </p>
            {sectorData.google_taxonomy_root_path && (
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                {sectorData.google_taxonomy_root_path}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {availableSectors.length > 1 ? (
              <>
                <Select
                  value={sectorData?.id?.toString() || ''}
                  onValueChange={handleSectorChange}
                  disabled={isChangingSector}
                >
                  <SelectTrigger className="h-auto py-2 px-4 border-2 rounded-full font-medium text-base">
                    <SelectValue placeholder="Choose a sector..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSectors.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isChangingSector && (
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </>
            ) : (
              <Badge variant="outline" className="text-lg px-4 py-2">
                {sectorData.name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Sector Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sector Image */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Sector Image
              </span>
              {isSuperAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={generateSectorImage}
                  disabled={generatingImage}
                  className="h-8 w-8 p-0"
                  title={sectorData.image_url ? "Regenerate image" : "Generate image"}
                >
                  {generatingImage ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={sectorData.image_url || `https://source.unsplash.com/400x200/?${encodeURIComponent(sectorData.name)},business,industry`}
              alt={sectorData.name}
              className="w-full h-48 object-cover rounded-lg"
              onError={(e) => {
                // Fallback to a generated placeholder if Unsplash fails
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(sectorData.name)}&background=6366F1&color=fff&size=400&length=3&font-size=0.25`;
              }}
            />
          </CardContent>
        </Card>

        {/* Sector Description */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Sector Overview</CardTitle>
            <CardDescription>Understanding the {sectorData.name} Industry</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sector Description */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Description
              </h4>
              <p className="text-sm text-muted-foreground">{sectorData.description}</p>
            </div>
            
            {/* Common Features Customers Evaluate */}
            <Collapsible defaultOpen={false}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full justify-between hover:bg-gray-50 p-2 rounded">
                <h4 className="font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Common Features Customers Evaluate When Choosing Products/Services
                </h4>
                <ChevronRight className="h-4 w-4 ui-open:rotate-90 transition-transform" />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                {sectorData.baselineFeatures?.products_or_services ? (
                  <div className="space-y-3">
                    {sectorData.baselineFeatures.products_or_services.map((product, index) => (
                      <div key={index} className="border-l-2 border-blue-200 pl-3">
                        <p className="font-medium text-sm mb-1">
                          {product.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {product.baseline_features?.map((feature, i) => (
                            <span
                              key={i}
                              className="inline-block px-2 py-0.5 text-xs bg-gray-100 rounded"
                            >
                              {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          )) || <span className="text-xs text-muted-foreground">No features specified</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(sectorData.characteristics || {})
                      .filter(([key]) => 
                        ['materials', 'price_range', 'certifications', 'delivery_methods', 'typical_age_range'].includes(key)
                      )
                      .map(([key, value]) => (
                        <div key={key} className="space-y-1">
                          <span className="text-xs text-muted-foreground">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <p className="text-sm font-medium">
                            {Array.isArray(value) ? value.join(', ') || 'N/A' : value || 'N/A'}
                          </p>
                        </div>
                      ))}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="categories">
            <Package className="h-4 w-4 mr-2" />
            Sector Categories
          </TabsTrigger>
          <TabsTrigger value="product-intelligence">
            <Sparkles className="h-4 w-4 mr-2" />
            Product Intelligence
          </TabsTrigger>
          <TabsTrigger value="trends">
            <TrendingUp className="h-4 w-4 mr-2" />
            Latest Trends
          </TabsTrigger>
          <TabsTrigger value="topics">
            <BookOpen className="h-4 w-4 mr-2" />
            Research Topics
          </TabsTrigger>
          <TabsTrigger value="wheels">
            <HelpCircle className="h-4 w-4 mr-2" />
            Question Wheels
          </TabsTrigger>
          <TabsTrigger value="segments">
            <Users className="h-4 w-4 mr-2" />
            Market Segments
          </TabsTrigger>
          <TabsTrigger value="personas">
            <User className="h-4 w-4 mr-2" />
            Personas
          </TabsTrigger>
          <TabsTrigger value="insights">
            <Target className="h-4 w-4 mr-2" />
            Strategic Insights
          </TabsTrigger>
        </TabsList>

        {/* Product Intelligence Tab */}
        <TabsContent value="product-intelligence">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Product Intelligence Research
                  </CardTitle>
                  <CardDescription>
                    Approved customer perception research for product categories
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingResearch ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : approvedResearch.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {approvedResearch.map((research) => (
                    <Card key={research.sessionId} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base">{research.categoryName}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              {research.attributesCount} perceived attributes
                            </p>
                          </div>
                          <Badge variant="default" className="bg-green-500">
                            <Check className="h-3 w-3 mr-1" />
                            Approved
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(research.reviewedAt).toLocaleDateString()}
                          </span>
                          <span>By {research.reviewedByName}</span>
                        </div>

                        {/* Top Perceived Benefits */}
                        {research.topBenefits && research.topBenefits.length > 0 && (
                          <div className="mb-3 space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Top Perceived Benefits:</p>
                            <div className="space-y-1">
                              {research.topBenefits.slice(0, 3).map((benefit: any, idx: number) => (
                                <div key={idx} className="flex items-start gap-2 text-xs">
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                    {benefit.benefit_layer}
                                  </Badge>
                                  <span className="text-muted-foreground flex-1">{benefit.attribute_name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewPerceptionResearch(research.categoryName)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View Research
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => generateQuestionsFromResearch(research.categoryName)}
                            disabled={generatingQuestions === research.categoryName}
                          >
                            {generatingQuestions === research.categoryName ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <HelpCircle className="h-3 w-3 mr-1" />
                                Generate Questions
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No approved product intelligence research available yet for this sector.
                    {isSuperAdmin && (
                      <span className="block mt-2">
                        As a super admin, you can trigger research in the{' '}
                        <a href="/admin/research-management" className="text-primary underline">
                          Research Management
                        </a>{' '}
                        section.
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Benefit-Attribute Mappings Card */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Benefit-Attribute Mappings
                  </CardTitle>
                  <CardDescription>
                    AI-powered mappings between customer benefits and product attributes
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = '/research/benefit-mappings'}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View All
                  </Button>
                  <Button
                    size="sm"
                    onClick={extendMappingCoverage}
                    disabled={extendingCoverage || loadingMappings}
                  >
                    {extendingCoverage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Extending...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-1" />
                        Extend Coverage
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingMappings ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : mappingsStats ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-blue-900">
                          Benefits Defined
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-700">
                          {mappingsStats.benefits}
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          Customer benefit types
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-green-900">
                          Attribute Mappings
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-700">
                          {mappingsStats.mappings}
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          Benefit-attribute links
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-purple-900">
                          Value Ranges
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-700">
                          {mappingsStats.valueRanges}
                        </div>
                        <p className="text-xs text-purple-600 mt-1">
                          Defined attribute ranges
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {mappingsStats.benefits === 0 ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        No benefit-attribute mappings exist yet for this sector.
                        Click "Extend Coverage" to automatically generate mappings using AI.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <Sparkles className="h-4 w-4" />
                      <AlertDescription>
                        Your sector has {mappingsStats.benefits} benefit types mapped to product attributes.
                        This enables intelligent product recommendations based on customer needs.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load mapping statistics. Please try refreshing the page.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Latest Industry Trends</CardTitle>
                  <CardDescription>
                    Trends and news from the last 30 days
                    {sectorData.last_trends_update && (
                      <span className="ml-2 text-xs">
                        Last updated: {new Date(sectorData.last_trends_update).toLocaleDateString()}
                      </span>
                    )}
                  </CardDescription>
                </div>
                {!isContentManager && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshTrends}
                    disabled={refreshingTrends}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshingTrends ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {trends.length > 0 ? (
                <div className="space-y-4">
                  {trends.map((trend) => (
                    <div
                      key={trend.id}
                      className="border-l-4 border-primary pl-4 py-2 hover:bg-muted/50 cursor-pointer transition-colors rounded-r"
                      onClick={() => handleOpenTrendDetail(trend)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{trend.trend_title}</h4>
                            {trend.has_research && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                <BookOpen className="h-3 w-3 mr-1" />
                                Research
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {trend.trend_description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(trend.trend_date).toLocaleDateString()}
                            </span>
                            <span>{trend.trend_source}</span>
                            {trend.relevance_score > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                Relevance: {trend.relevance_score > 1 ? trend.relevance_score.toFixed(0) : (trend.relevance_score * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTrendDetail(trend);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                          {trend.trend_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              onClick={(e) => e.stopPropagation()}
                            >
                              <a href={trend.trend_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4 mr-1" />
                                Source
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No trends available yet. Click refresh to fetch the latest industry trends.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research Topics Tab */}
        <TabsContent value="topics">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Researched Topics</CardTitle>
                  <CardDescription>
                    In-depth research conducted for the {sectorData.name} sector
                  </CardDescription>
                </div>
                {isSuperAdmin && (
                  <Button
                    onClick={() => setProductSelectionModal({ open: true, mode: 'research' })}
                    variant="default"
                    size="sm"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Trigger Research
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {topics.length > 0 ? (
                <ScrollArea className="h-[600px] pr-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {topics.map((topic) => (
                      <Card key={topic.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base">{topic.topic_title}</CardTitle>
                          <div className="flex gap-2">
                            {topic.topic_type && (
                              <Badge variant="secondary">{topic.topic_type}</Badge>
                            )}
                            {topic.relevance_score && (
                              <Badge variant="outline">
                                {Math.round(topic.relevance_score * 100)}% relevance
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {topic.description || 'No description available'}
                        </p>
                        {topic.category && (
                          <div className="mt-2">
                            <Badge variant="outline" className="text-xs">
                              {topic.category}
                            </Badge>
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-xs text-muted-foreground">
                            {new Date(topic.research_date).toLocaleDateString()}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-3">
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 flex items-center gap-1 text-muted-foreground hover:text-primary"
                                onClick={() => handleViewResearch(topic.id)}
                              >
                                <FileText className="h-3 w-3" />
                                View Research
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 flex items-center gap-1 text-primary"
                                onClick={() => setAiWriterModal({
                                  open: true,
                                  topic: {
                                    id: topic.id,
                                    topic_name: topic.topic_title,
                                    topic_description: topic.description,
                                    category: topic.category,
                                    topic_type: topic.topic_type,
                                    top_categories: topic.top_categories
                                  }
                                })}
                              >
                                <PenTool className="h-3 w-3" />
                                AI Writer
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  </div>
                </ScrollArea>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No research topics available yet. Topics will appear as sector analysis progresses.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Question Wheels Tab */}
        <TabsContent value="wheels">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Sector Question Wheels
                  </CardTitle>
                  <CardDescription>
                    Organized collections of sector-specific question wheels by category
                  </CardDescription>
                </div>
                {isSuperAdmin && (
                  <Button
                    onClick={() => setProductSelectionModal({ open: true, mode: 'wheel' })}
                    variant="default"
                    size="sm"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Generate Wheel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingWheels ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : questionWheels.length > 0 ? (
                <QuestionWheelCategoryCards
                  wheels={questionWheels}
                  onCategoryClick={(category) => {
                    // Navigate to question wheel research page with sector context and category filter
                    window.location.href = `/research/question-wheel?sector=${sectorData?.id}&category=${category}`;
                  }}
                />
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No question wheels have been generated for this sector yet. Question wheels will be created during the Q&A pipeline execution.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sector Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Product Category Taxonomy
                  </CardTitle>
                  <CardDescription>
                    Explore the hierarchical product category structure for {sectorData.name}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-1 border rounded-lg p-1">
                    <Button
                      variant={categoryViewMode === 'wheel' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCategoryViewMode('wheel')}
                      className="h-8 px-3"
                    >
                      <Grid3x3 className="h-4 w-4 mr-1" />
                      Wheel
                    </Button>
                    <Button
                      variant={categoryViewMode === 'tree' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCategoryViewMode('tree')}
                      className="h-8 px-3"
                    >
                      <List className="h-4 w-4 mr-1" />
                      Tree
                    </Button>
                  </div>

                  {/* View Market selector - show when multiple markets have data */}
                  {enrichedMarkets.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">Data:</span>
                      <Select value={viewMarket} onValueChange={setViewMarket}>
                        <SelectTrigger className="w-[130px] h-9">
                          <TrendingUp className="h-4 w-4 mr-1" />
                          <SelectValue placeholder="Market" />
                        </SelectTrigger>
                        <SelectContent>
                          {enrichedMarkets.map((market) => (
                            <SelectItem key={market.market} value={market.market}>
                              {market.market}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Translation loading indicator */}
                  {isLoadingTranslations && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Translating...</span>
                    </div>
                  )}

                  {isSuperAdmin && (
                    <div className="flex gap-2">
                      <Button
                        onClick={handleEnrichTaxonomy}
                        disabled={isEnrichingTaxonomy || isEnrichingWithGoogleAds || isGeneratingFromProducts}
                        className="shadow-md relative z-50 text-sm"
                        size="sm"
                        title={taxonomyData.length === 0
                          ? "Initialize from Google Product Taxonomy"
                          : "Generate AI-powered sub-categories"}
                      >
                        {isEnrichingTaxonomy ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        {taxonomyData.length === 0 ? 'Initialize Taxonomy' : 'Generate Sub-Categories'}
                      </Button>
                      <Button
                        onClick={handleGenerateFromProducts}
                        disabled={isGeneratingFromProducts || isEnrichingTaxonomy || isEnrichingWithGoogleAds}
                        variant="outline"
                        size="sm"
                        className="shadow-md relative z-50 text-sm"
                        title="Generate categories from imported product data"
                      >
                        {isGeneratingFromProducts ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Package className="h-4 w-4 mr-2" />
                        )}
                        Generate from Products
                      </Button>
                      {taxonomyData.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Select value={selectedMarket} onValueChange={setSelectedMarket}>
                            <SelectTrigger className="w-[160px] h-9">
                              <SelectValue placeholder="Select market" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableMarkets.map((market) => (
                                <SelectItem key={market.market_code} value={market.market_name}>
                                  {market.market_name}
                                  {enrichedMarkets.find(m => m.market === market.market_name) && (
                                    <span className="ml-1 text-green-500">*</span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={handleEnrichWithGoogleAds}
                            disabled={isEnrichingWithGoogleAds || isEnrichingTaxonomy}
                            variant="outline"
                            size="sm"
                            className="shadow-md relative z-50 text-sm"
                            title={`Enrich categories with Google Ads data for ${selectedMarket}`}
                          >
                            {isEnrichingWithGoogleAds ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <TrendingUp className="h-4 w-4 mr-2" />
                            )}
                            Enrich with Google Ads
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Wheel View */}
              {categoryViewMode === 'wheel' && (
                <>
                  {/* Breadcrumb navigation */}
                  {taxonomyBreadcrumb.length > 0 && (
                    <div className="mb-4 flex items-center gap-2 text-sm flex-wrap">
                      <button
                        onClick={() => {
                          setTaxonomyBreadcrumb([]);
                          setCurrentTaxonomyView(null);
                          if (sectorData?.id) loadTaxonomyData(sectorData.id);
                        }}
                        className="text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                      >
                        {sectorData.name}
                      </button>
                      {taxonomyBreadcrumb.map((crumb, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          <button
                            onClick={() => handleTaxonomyBreadcrumbClick(index)}
                            className={`transition-colors cursor-pointer ${
                              index === taxonomyBreadcrumb.length - 1
                                ? "text-foreground font-semibold"
                                : "text-muted-foreground hover:text-primary"
                            }`}
                          >
                            {categoryTranslations[crumb.id] || crumb.category_name}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Demand Wheel Visualization */}
                  {taxonomyData.length > 0 ? (
                    <div className="w-full h-[900px] flex items-center justify-center overflow-hidden relative">
                      <DemandWheel
                        data={taxonomyData.map(cat => ({
                          ...cat,
                          display_name: categoryTranslations[cat.id] || cat.category_name
                        }))}
                        onCategoryClick={handleCategoryClick}
                        centerLabel={
                          currentTaxonomyView
                            ? (categoryTranslations[currentTaxonomyView.id] || currentTaxonomyView.category_name)
                            : sectorData.name
                        }
                        centerScore={
                          parseInt(currentTaxonomyView?.avg_monthly_searches) ||
                          taxonomyData.reduce((sum, cat) => sum + (parseInt(cat.avg_monthly_searches) || 0), 0)
                        }
                      />
                    </div>
                  ) : (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        No product categories available yet.
                        {isSuperAdmin && (
                          <span> Click "Enrich Categories" to generate an AI-powered taxonomy with search demand data.</span>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {/* Tree View */}
              {categoryViewMode === 'tree' && (
                <>
                  {loadingTreeView ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                      <span className="ml-3 text-muted-foreground">Loading category tree...</span>
                    </div>
                  ) : treeCategories.length > 0 ? (
                    <div className="space-y-2">
                      {treeCategories.map((category) => (
                        <TreeNode
                          key={category.id}
                          node={category}
                          level={0}
                          expandedNodes={expandedNodes}
                          loadingNodes={loadingNodes}
                          onToggle={toggleNode}
                          onTogglePrimary={handleTogglePrimary}
                          onSchemaOrgUpdate={handleSchemaOrgUpdate}
                          onLeafClick={handleLeafClick}
                          isSuperAdmin={isSuperAdmin}
                        />
                      ))}
                    </div>
                  ) : (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        No product categories available yet.
                        {isSuperAdmin && (
                          <span> Click "Enrich Categories" to generate an AI-powered taxonomy with search demand data.</span>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {/* Legend */}
              {taxonomyData.length > 0 && (
                <div className="mt-6 bg-muted/30 rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3">Category Demand Intensity</h4>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-green-600 dark:bg-green-500"></div>
                      <div className="text-xs">
                        <div className="font-medium">High Demand</div>
                        <div className="text-muted-foreground">Score: 70-100</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-yellow-600 dark:bg-yellow-500"></div>
                      <div className="text-xs">
                        <div className="font-medium">Medium Demand</div>
                        <div className="text-muted-foreground">Score: 50-69</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-red-600 dark:bg-red-500"></div>
                      <div className="text-xs">
                        <div className="font-medium">Low Demand</div>
                        <div className="text-muted-foreground">Score: 0-49</div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 italic">
                    Click on any category to explore subcategories. Demand scores are based on monthly search volume.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Personas Tab */}
        <TabsContent value="personas">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Buyer Personas
                  </CardTitle>
                  <CardDescription>
                    10 key buyer personas for the {sectorData.name} sector, based on market research and behavioral analysis
                  </CardDescription>
                </div>
                {!isContentManager && (
                  <Button
                    onClick={handleAIAutoMap}
                    disabled={autoMappingInProgress || personas.length === 0}
                    variant="default"
                    size="sm"
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    {autoMappingInProgress ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {jobProgress ? (
                          <span className="text-xs">
                            Mapping... {jobProgress.percentage}% ({jobProgress.processed}/{jobProgress.total})
                          </span>
                        ) : (
                          'Starting...'
                        )}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        AI Auto-Map Benefits
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Translation loading indicator */}
              {isTranslatingPersonas && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Translating personas...</span>
                </div>
              )}
              {personas.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {(translatedPersonas.length > 0 ? translatedPersonas : personas).slice(0, 10).map((persona, index) => (
                    <Card key={index} className="bg-gradient-to-br from-background to-muted/20 hover:shadow-lg transition-all duration-300">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                              <img
                                src={persona.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.name || `Persona ${index + 1}`)}&background=${['8B5CF6', '3B82F6', '10B981', 'F59E0B', 'EF4444', 'EC4899'][index % 6]}&color=fff&size=200&font-size=0.35&bold=true`}
                                alt={persona.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(persona.name || `Persona ${index + 1}`)}&background=8B5CF6&color=fff&size=200`;
                                }}
                              />
                            </div>
                            {isSuperAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => regeneratePersonaImage(persona.id, persona.name)}
                                disabled={regeneratingPersonas.has(persona.id)}
                                className="absolute -bottom-1 -right-1 h-7 w-7 p-0 rounded-full bg-background shadow-md hover:bg-primary/10"
                                title={persona.image_url ? "Regenerate image" : "Generate image"}
                              >
                                {regeneratingPersonas.has(persona.id) ? (
                                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Sparkles className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg">{persona.name}</CardTitle>
                            <p className="text-sm text-muted-foreground">{persona.occupation}</p>
                            {persona.age_range && (
                              <Badge variant="outline" className="mt-1">
                                <Clock className="h-3 w-3 mr-1" />
                                {persona.age_range}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {persona.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {persona.description}
                          </p>
                        )}

                        {persona.buying_behavior && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <ShoppingCart className="h-3 w-3" />
                              Buying Behavior
                            </h5>
                            <p className="text-xs text-muted-foreground">
                              {persona.buying_behavior}
                            </p>
                          </div>
                        )}

                        {persona.expectations && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              Expectations
                            </h5>
                            <p className="text-xs text-muted-foreground">
                              {persona.expectations}
                            </p>
                          </div>
                        )}

                        {persona.pain_points && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Pain Points
                            </h5>
                            <p className="text-xs text-muted-foreground">
                              {persona.pain_points}
                            </p>
                          </div>
                        )}

                        {persona.preferred_features && Array.isArray(persona.preferred_features) && persona.preferred_features.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium flex items-center gap-1">
                              <Heart className="h-3 w-3" />
                              Preferred Features
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {persona.preferred_features.slice(0, 4).map((feature: string, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {feature}
                                </Badge>
                              ))}
                              {persona.preferred_features.length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{persona.preferred_features.length - 4} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Mapped Benefits Section */}
                        {(() => {
                          const categoryTemplates = personaBenefits.get(persona.name) || [];
                          const totalBenefits = categoryTemplates.reduce((sum: number, cat: any) => sum + (cat.benefits?.length || 0), 0);
                          return (
                            <div className="space-y-2 pt-2 border-t">
                              <div className="flex items-center justify-between">
                                <h5 className="text-sm font-medium flex items-center gap-1">
                                  <Target className="h-3 w-3" />
                                  Priority Benefits ({totalBenefits} total)
                                </h5>
                                {categoryTemplates.length > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                      setBenefitDetailDialog({
                                        open: true,
                                        personaName: persona.name,
                                        personaDescription: persona.description,
                                        categoryTemplates,
                                      });
                                    }}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View All
                                  </Button>
                                )}
                              </div>
                              {categoryTemplates.length > 0 ? (
                                <div className="space-y-3">
                                  {categoryTemplates.slice(0, 3).map((category: any) => (
                                    <div key={category.template_id} className="space-y-1">
                                      <p className="text-xs font-medium text-muted-foreground">
                                        {category.category_name}
                                      </p>
                                      <div className="flex flex-wrap gap-1">
                                        {category.benefits?.slice(0, 3).map((benefit: any) => (
                                          <Badge
                                            key={benefit.benefit_name}
                                            variant="secondary"
                                            className="text-xs"
                                            title={`${benefit.notes || ''}\nRank ${benefit.priority_rank} | Weight ${benefit.priority_weight}`}
                                          >
                                            {benefit.benefit_name}
                                            <span className="ml-1 text-[10px] opacity-60">
                                              #{benefit.priority_rank}
                                            </span>
                                          </Badge>
                                        ))}
                                        {category.benefits && category.benefits.length > 3 && (
                                          <Badge variant="outline" className="text-xs">
                                            +{category.benefits.length - 3}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {categoryTemplates.length > 3 && (
                                    <p className="text-xs text-muted-foreground italic">
                                      + {categoryTemplates.length - 3} more categories
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">
                                  No benefits mapped yet
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        {/* AI Writer Button */}
                        <div className="pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full flex items-center gap-2"
                            onClick={() => {
                              setAiWriterPersonaModal({
                                open: true,
                                persona: persona
                              });
                            }}
                          >
                            <PenTool className="h-4 w-4" />
                            Generate Content for This Persona
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No buyer personas available yet. Personas are generated based on sector analysis and market research.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Market Segments Tab */}
        <TabsContent value="segments">
          <div className="space-y-6">
            {/* Common Market Segments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Market Segments
                </CardTitle>
                <CardDescription>
                  Typical market segments in the {sectorData.name} sector based on market research
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sectorData.common_segments.map((segment, index) => (
                    <Card key={index} className="bg-muted/30 border-dashed">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {segment.name}
                        </CardTitle>
                        <Badge variant="outline" className="w-fit">Market Research</Badge>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {segment.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Actual Site Behavioral Segments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Your Site's Behavioral Segments
                </CardTitle>
                <CardDescription>
                  Data-driven segments based on actual visitor behavior (discount usage, browsing patterns, engagement)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {siteSegments.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {siteSegments.map((segment, index) => (
                        <Card key={index} className="bg-primary/5 border-primary/20 hover:shadow-md transition-shadow">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-base flex items-center gap-2">
                                <Target className="h-4 w-4 text-primary" />
                                {segment.segment_name}
                              </CardTitle>
                              <Badge variant="default" className="bg-primary/20 text-primary">
                                Behavioral
                              </Badge>
                            </div>
                            {segment.size && (
                              <p className="text-xs text-muted-foreground">
                                {segment.size} visitors
                              </p>
                            )}
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground mb-3">
                              {segment.description}
                            </p>
                            {segment.behaviors && Array.isArray(segment.behaviors) && (
                              <div className="space-y-2">
                                <h6 className="text-xs font-medium text-muted-foreground">Key Behaviors:</h6>
                                <div className="flex flex-wrap gap-1">
                                  {segment.behaviors.slice(0, 3).map((behavior: string, idx: number) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {behavior}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <strong>Smart Segmentation:</strong> As PageMind.ai observes activity on your website, 
                        customer segmentation becomes more accurate and personalized to your specific audience.
                      </AlertDescription>
                    </Alert>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h4 className="text-lg font-medium mb-2">Building Your Customer Segments</h4>
                    <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                      PageMind.ai is analyzing visitor behavior on your website to create personalized customer segments.
                    </p>
                    <Alert className="bg-blue-50 border-blue-200 max-w-2xl mx-auto">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <strong>How it works:</strong> As PageMind.ai observes activity on your website, 
                        customer segmentation becomes more accurate and personalized to your specific audience. 
                        Check back as your site receives more traffic.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Strategic Insights Tab */}
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Strategic Insights</CardTitle>
                  <CardDescription>
                    Sector-specific analysis and actionable recommendations
                    {sectorData.last_insights_update && (
                      <span className="ml-2 text-xs">
                        Last updated: {new Date(sectorData.last_insights_update).toLocaleDateString()}
                      </span>
                    )}
                  </CardDescription>
                </div>
                {!isContentManager && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        setRefreshingInsights(true);
                        const response = await authFetch(`/sector-research/${sectorData.id}/refresh-strategic-insights`, {
                          method: 'POST'
                        });
                        if (response.ok) {
                          const result = await response.json();
                          // Update sector data with new insights
                          setSectorData({
                            ...sectorData,
                            strategic_insights: result.insights,
                            last_insights_update: new Date().toISOString()
                          });
                          toast({
                            title: 'Success',
                            description: `Generated ${result.insights.length} strategic insights`,
                          });
                        }
                      } catch (error) {
                        console.error('Error refreshing insights:', error);
                        toast({
                          title: 'Error',
                          description: 'Failed to refresh strategic insights',
                          variant: 'destructive'
                        });
                      } finally {
                        setRefreshingInsights(false);
                      }
                    }}
                    disabled={refreshingInsights}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshingInsights ? 'animate-spin' : ''}`} />
                    {refreshingInsights ? 'Generating...' : 'Refresh Insights'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {sectorData.strategic_insights && sectorData.strategic_insights.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {sectorData.strategic_insights.map((insight: any, index: number) => {
                    const categoryIcons: Record<string, any> = {
                      market_positioning: Target,
                      growth_opportunities: TrendingUp,
                      competitive_advantage: Sparkles,
                      customer_engagement: Users,
                      content_strategy: FileText,
                      technology_trends: Package,
                      regulatory_landscape: AlertCircle
                    };

                    const Icon = categoryIcons[insight.category] || Target;

                    const impactColors: Record<string, string> = {
                      high: 'text-green-600 dark:text-green-400',
                      medium: 'text-yellow-600 dark:text-yellow-400',
                      low: 'text-gray-600 dark:text-gray-400'
                    };

                    return (
                      <Card key={index} className="bg-muted/30">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 flex-1">
                              <Icon className="h-5 w-5 mt-0.5 text-primary" />
                              <div className="flex-1">
                                <CardTitle className="text-base leading-tight">
                                  {insight.title}
                                </CardTitle>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {insight.category.replace(/_/g, ' ')}
                                  </Badge>
                                  <Badge variant="secondary" className={`text-xs ${impactColors[insight.impact_level]}`}>
                                    {insight.impact_level} impact
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            {insight.description}
                          </p>

                          {insight.actionable_recommendations && insight.actionable_recommendations.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-foreground">
                                Action Items:
                              </div>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                {insight.actionable_recommendations.map((rec: string, i: number) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary" />
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                            <Clock className="h-3 w-3" />
                            <span className="capitalize">{insight.timeframe.replace(/_/g, ' ')} timeframe</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    No strategic insights available yet. Generate sector research to get AI-powered insights
                    specific to the {sectorData.name} industry.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Research Modal */}
      <Dialog
        open={researchModal.open}
        onOpenChange={(open) => setResearchModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{researchModal.title}</DialogTitle>
            <DialogDescription>
              Research findings and insights
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] mt-4">
            {researchModal.loading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : researchModal.perceptions && researchModal.perceptions.length > 0 ? (
              <div className="space-y-6 pr-4">
                {/* Translation loading indicator */}
                {isTranslatingPerceptions && language !== 'en' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground pb-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Translating content...</span>
                  </div>
                )}
                {/* Group attributes by benefit layer */}
                {['functional', 'emotional', 'aspirational'].map((layer) => {
                  const perceptionsToUse = translatedPerceptions.length > 0 ? translatedPerceptions : researchModal.perceptions;
                  const layerAttrs = (perceptionsToUse || []).filter(
                    (attr: any) => (attr.benefit_layer || 'functional') === layer
                  );

                  if (layerAttrs.length === 0) return null;

                  const layerInfo = {
                    functional: { emoji: '🧠', title: 'Functional Benefits', desc: 'Practical & Measurable', color: 'text-blue-600' },
                    emotional: { emoji: '❤️', title: 'Emotional Benefits', desc: 'Feelings & Confidence', color: 'text-red-600' },
                    aspirational: { emoji: '🌍', title: 'Aspirational Benefits', desc: 'Lifestyle & Identity', color: 'text-green-600' },
                  }[layer];

                  return (
                    <div key={layer} className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <span className="text-2xl">{layerInfo.emoji}</span>
                        <div>
                          <h3 className={`text-lg font-semibold ${layerInfo.color}`}>
                            {layerInfo.title}
                          </h3>
                          <p className="text-xs text-muted-foreground">{layerInfo.desc}</p>
                        </div>
                        <Badge variant="outline" className="ml-auto">
                          {layerAttrs.length} attributes
                        </Badge>
                      </div>

                      {layerAttrs.map((attr: any) => (
                        <Card key={attr.id} className="border-l-4" style={{ borderLeftColor: layerInfo.color.replace('text-', '#') }}>
                          <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                              {attr.attribute_name}
                              <Badge className={getAttributeTypeBadge(attr.attribute_type)}>
                                {attr.attribute_type.replace('_', ' ')}
                              </Badge>
                              {attr.purchase_driver_strength && (
                                <Badge variant="default" className="bg-purple-100 text-purple-800">
                                  Purchase Impact: {attr.purchase_driver_strength}/10
                                </Badge>
                              )}
                              {attr.research_quality_score && (
                                <Badge variant="outline">
                                  Quality: {Math.round(attr.research_quality_score * 100)}%
                                </Badge>
                              )}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              {attr.customer_description}
                            </p>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {/* Common Phrases */}
                            {attr.common_phrases && attr.common_phrases.length > 0 && (
                              <div>
                                <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                                  Common Customer Phrases
                                </h5>
                                <div className="flex flex-wrap gap-1">
                                  {attr.common_phrases.map((phrase: string, idx: number) => (
                                    <Badge key={idx} variant="secondary">
                                      {phrase}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Technical Features */}
                            {attr.technical_features && attr.technical_features.length > 0 && (
                              <div>
                                <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                                  Related Technical Features
                                </h5>
                                <div className="flex flex-wrap gap-1">
                                  {attr.technical_features.map((feature: string, idx: number) => (
                                    <Badge key={idx} variant="outline">
                                      {feature}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No perceived attributes found for this category. The research may not have generated any attributes yet, or they haven't been validated.
                </AlertDescription>
              </Alert>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Topic Research Modal */}
      <Dialog
        open={topicResearchModal.open}
        onOpenChange={(open) => setTopicResearchModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{topicResearchModal.title}</DialogTitle>
            <DialogDescription>
              Research findings and insights
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] mt-4">
            {topicResearchModal.loading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none pr-4 prose-headings:mt-6 prose-headings:mb-3 prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:mb-4 prose-p:leading-7 prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6 prose-li:mb-2 prose-strong:font-semibold prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800">
                <div
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      let content = topicResearchModal.content || 'No research content available.';
                      // Convert markdown-style formatting to HTML
                      return content
                        // First, normalize line breaks
                        .replace(/\s*\n\s*/g, '\n')
                        // Replace sentence-ending punctuation followed by space and capital/bold with line break
                        .replace(/([.!?])\s+(\*\*\d+\.|\*\*[A-Z])/g, '$1\n$2')
                        // Convert **Number. Title** to headings
                        .replace(/\*\*(\d+)\.\s+([^*]+)\*\*/g, '\n<h3 class="text-lg font-bold mt-6 mb-3 text-gray-900">$1. $2</h3>\n')
                        // Convert remaining **bold** to <strong>
                        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                        // Split into paragraphs by line breaks
                        .split('\n')
                        .map(para => para.trim())
                        .filter(para => para.length > 0)
                        .map(para => {
                          // Don't wrap if it's already an HTML tag
                          if (para.startsWith('<h')) return para;
                          return `<p class="mb-4 leading-7">${para}</p>`;
                        })
                        .join('\n');
                    })()
                  }}
                />
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Generated Questions Modal */}
      <Dialog
        open={questionsModal.open}
        onOpenChange={(open) => setQuestionsModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{questionsModal.title}</DialogTitle>
                <DialogDescription>
                  AI-generated customer questions based on perceived attributes
                </DialogDescription>
              </div>
              {questionsModal.cached && !questionsModal.loading && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generateQuestionsFromResearch(questionsModal.categoryName, true)}
                  disabled={generatingQuestions === questionsModal.categoryName}
                >
                  {generatingQuestions === questionsModal.categoryName ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Regenerate
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogHeader>
          {questionsModal.cached && !questionsModal.loading && (
            <Alert className="border-green-200 bg-green-50 mt-4">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="ml-2 text-green-900">
                <strong>Using cached questions</strong> - These questions were previously generated and saved to avoid AI costs.
              </AlertDescription>
            </Alert>
          )}
          <ScrollArea className="h-[60vh] mt-4">
            {questionsModal.loading ? (
              <div className="space-y-4">
                <Alert className="border-blue-200 bg-blue-50">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <AlertDescription className="ml-2 text-blue-900">
                    <strong>AI is analyzing perceived attributes...</strong>
                    <p className="text-sm mt-1 text-blue-700">
                      Generating customer questions based on the research insights. This typically takes 10-30 seconds.
                    </p>
                  </AlertDescription>
                </Alert>
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : questionsModal.questions.length > 0 ? (
              <div className="space-y-3">
                {questionsModal.questions.map((q, index) => (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="font-medium text-base mb-2">{q.question}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {q.attributeName}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {q.attributeType?.replace('_', ' ')}
                            </Badge>
                            {q.intent && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                {q.intent.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                          {q.metadata?.commonPhrases && q.metadata.commonPhrases.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              Common phrases: {q.metadata.commonPhrases.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  No questions generated. This may happen if there are no approved perceptions for this category.
                </AlertDescription>
              </Alert>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* AI Writer Modal */}
      {aiWriterModal.topic && (
        <AIWriterModal
          open={aiWriterModal.open}
          onOpenChange={(open) => setAiWriterModal({ open, topic: aiWriterModal.topic })}
          topic={aiWriterModal.topic}
          onArticleGenerated={(article) => {
            setGeneratedArticle(article);
            setAiWriterModal({ open: false, topic: null });
          }}
        />
      )}

      {/* Article Preview Modal */}
      {generatedArticle && (
        <AIWriterPreview
          open={!!generatedArticle}
          onOpenChange={(open) => !open && setGeneratedArticle(null)}
          article={generatedArticle}
          onUpdate={(updated) => setGeneratedArticle(updated)}
        />
      )}

      {/* Trend Detail Modal */}
      <Dialog open={trendDetailModal.open} onOpenChange={(open) => {
        setTrendDetailModal({ open, trend: null });
        if (!open) {
          setTrendResearch(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              {trendDetailModal.trend?.trend_title}
              {trendResearch && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  <BookOpen className="h-3 w-3 mr-1" />
                  Research Available
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {trendDetailModal.trend && (
            <div className="space-y-6">
              {/* Metadata */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(trendDetailModal.trend.trend_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                {trendDetailModal.trend.trend_source && (
                  <Badge variant="outline">
                    {trendDetailModal.trend.trend_source}
                  </Badge>
                )}
                {trendDetailModal.trend.relevance_score > 0 && (
                  <Badge variant="secondary">
                    {trendDetailModal.trend.relevance_score > 1 ? trendDetailModal.trend.relevance_score.toFixed(0) : (trendDetailModal.trend.relevance_score * 100).toFixed(0)}% Relevance
                  </Badge>
                )}
              </div>

              {/* Description */}
              <div className="prose prose-sm max-w-none">
                <h3 className="text-lg font-semibold mb-3">Description</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {trendDetailModal.trend.trend_description}
                </p>
              </div>

              {/* Source URL if available */}
              {trendDetailModal.trend.trend_url && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={trendDetailModal.trend.trend_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Original Source
                    </a>
                  </Button>
                </div>
              )}

              {/* Research Section */}
              <div className="pt-4 border-t">
                {loadingTrendResearch ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading research...</span>
                  </div>
                ) : trendResearch ? (
                  <div className="space-y-6">
                    {/* Research Header */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        Deep Research
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{trendResearch.word_count} words</Badge>
                        <Badge variant="outline" className="capitalize">{trendResearch.research_depth}</Badge>
                        {trendResearch.display_language && trendResearch.display_language !== 'en' && (
                          <Badge variant="secondary">
                            <Globe className="h-3 w-3 mr-1" />
                            {trendResearch.display_language.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Summary */}
                    {trendResearch.research_summary && (
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Summary
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {trendResearch.research_summary}
                        </p>
                      </div>
                    )}

                    {/* Key Findings */}
                    {trendResearch.key_findings && trendResearch.key_findings.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-500" />
                          Key Findings ({trendResearch.key_findings.length})
                        </h4>
                        <div className="grid gap-3">
                          {trendResearch.key_findings.map((finding, idx) => (
                            <div key={idx} className="bg-card border rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <Badge variant="outline" className="text-xs shrink-0 capitalize">
                                  {finding.category}
                                </Badge>
                                <div>
                                  <p className="text-sm font-medium">{finding.finding}</p>
                                  <p className="text-xs text-muted-foreground mt-1">{finding.importance}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Research Content (Collapsible) */}
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <span className="font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Full Research Document
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-3">
                        <div className="prose prose-sm max-w-none bg-card border rounded-lg p-4 max-h-[400px] overflow-auto">
                          <div dangerouslySetInnerHTML={{
                            __html: (trendResearch.research_content || '')
                              .replace(/\n\n/g, '</p><p>')
                              .replace(/\n/g, '<br/>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/^/, '<p>')
                              .replace(/$/, '</p>')
                          }} />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Sources */}
                    {trendResearch.sources && trendResearch.sources.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          Sources ({trendResearch.sources.length})
                          {trendResearch.citationSources?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {trendResearch.citationSources.length} citation-worthy
                            </Badge>
                          )}
                        </h4>
                        <div className="space-y-2">
                          {/* Sort sources: citation-worthy first, then by quality score */}
                          {[...trendResearch.sources]
                            .sort((a, b) => {
                              if (a.citationWorthy !== b.citationWorthy) {
                                return b.citationWorthy ? 1 : -1;
                              }
                              return (b.qualityScore || 0) - (a.qualityScore || 0);
                            })
                            .map((source, idx) => {
                            // Generate search URL as fallback for sources without URLs
                            const sourceUrl = source.url ||
                              `https://www.google.com/search?q=${encodeURIComponent(`${source.title} ${source.publication || ''}`)}`;

                            // Quality score color
                            const qualityColor = (source.qualityScore || 5) >= 8
                              ? 'text-green-600 dark:text-green-400'
                              : (source.qualityScore || 5) >= 6
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-muted-foreground';

                            // Source type badge color
                            const typeColors: Record<string, string> = {
                              news: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
                              magazine: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
                              academic: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
                              market_research: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
                            };

                            return (
                              <div
                                key={idx}
                                className={`text-sm border-l-2 pl-3 py-2 ${
                                  source.citationWorthy
                                    ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/20'
                                    : 'border-muted'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <a
                                    href={sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium text-primary hover:underline cursor-pointer flex items-center gap-1 flex-1"
                                  >
                                    {source.citationWorthy && (
                                      <span title="Citation-worthy source">📝</span>
                                    )}
                                    {source.title}
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  </a>
                                  {source.qualityScore && (
                                    <span className={`text-xs font-medium ${qualityColor}`} title="Quality Score">
                                      {source.qualityScore.toFixed(1)}/10
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {source.sourceType && source.sourceType !== 'unknown' && (
                                    <Badge
                                      variant="secondary"
                                      className={`text-xs ${typeColors[source.sourceType] || ''}`}
                                    >
                                      {source.sourceType.replace('_', ' ')}
                                    </Badge>
                                  )}
                                  {source.domainAuthority && source.domainAuthority !== 'medium' && (
                                    <Badge variant="outline" className="text-xs">
                                      {source.domainAuthority} authority
                                    </Badge>
                                  )}
                                  {source.publication && (
                                    <span className="text-xs text-muted-foreground">
                                      {source.publication}
                                    </span>
                                  )}
                                  {source.domain && !source.publication && (
                                    <span className="text-xs text-muted-foreground">
                                      {source.domain}
                                    </span>
                                  )}
                                </div>
                                {(source.snippet || source.excerpt) && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {source.excerpt || source.snippet}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Regenerate Button */}
                    <div className="flex justify-end pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateTrendResearch(trendDetailModal.trend!.id, 'comprehensive')}
                        disabled={!!generatingTrendResearch}
                      >
                        {generatingTrendResearch === 'comprehensive' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate (Comprehensive)
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 space-y-4">
                    <div className="text-muted-foreground">
                      <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No deep research available for this trend yet.</p>
                      <p className="text-sm">Generate comprehensive research with sources and key findings.</p>
                    </div>
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => generateTrendResearch(trendDetailModal.trend!.id, 'quick')}
                        disabled={!!generatingTrendResearch}
                      >
                        {generatingTrendResearch === 'quick' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Quick Research
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => generateTrendResearch(trendDetailModal.trend!.id, 'standard')}
                        disabled={!!generatingTrendResearch}
                      >
                        {generatingTrendResearch === 'standard' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <BookOpen className="h-4 w-4 mr-2" />
                            Standard Research
                          </>
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => generateTrendResearch(trendDetailModal.trend!.id, 'comprehensive')}
                        disabled={!!generatingTrendResearch}
                      >
                        {generatingTrendResearch === 'comprehensive' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileText className="h-4 w-4 mr-2" />
                            Comprehensive
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Writer Persona Modal */}
      {aiWriterPersonaModal.persona && selectedSite && (
        <AIWriterPersonaModal
          open={aiWriterPersonaModal.open}
          onOpenChange={(open) => {
            if (!open) {
              setAiWriterPersonaModal({ open: false, persona: null });
            }
          }}
          persona={aiWriterPersonaModal.persona}
          siteId={selectedSite.id || selectedSite._id}
        />
      )}

      {/* Product Selection Modal for Research & Wheel Generation */}
      {selectedSite && sectorData && (
        <ProductSelectionModal
          open={productSelectionModal.open}
          onOpenChange={(open) => setProductSelectionModal({ ...productSelectionModal, open })}
          siteId={selectedSite.id || selectedSite._id}
          sectorId={sectorData.id}
          mode={productSelectionModal.mode}
          onConfirm={productSelectionModal.mode === 'research' ? handleTriggerResearch : handleGenerateWheel}
        />
      )}

      {/* Persona-Benefit Mapping Dialog */}
      {sectorData && (
        <PersonaBenefitMappingDialog
          open={benefitMappingDialog.open}
          onOpenChange={(open) => setBenefitMappingDialog({ ...benefitMappingDialog, open })}
          sectorId={sectorData.id}
          mode={benefitMappingDialog.mode}
          personaName={benefitMappingDialog.personaName}
          personaDescription={benefitMappingDialog.personaDescription}
          benefitName={benefitMappingDialog.benefitName}
          benefitDescription={benefitMappingDialog.benefitDescription}
          onSuccess={() => {
            // Reload mappings
            if (benefitMappingDialog.personaName) {
              loadPersonaBenefits(benefitMappingDialog.personaName);
            }
            toast({
              title: 'Mappings updated',
              description: 'Benefit-persona mappings have been saved',
            });
          }}
        />
      )}

      {/* Benefit Detail View Dialog */}
      <Dialog open={benefitDetailDialog.open} onOpenChange={(open) => setBenefitDetailDialog({ ...benefitDetailDialog, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {benefitDetailDialog.personaName} - All Priority Benefits
            </DialogTitle>
            <DialogDescription>
              {benefitDetailDialog.personaDescription && (
                <span className="block mb-2">{benefitDetailDialog.personaDescription}</span>
              )}
              Complete list of all {benefitDetailDialog.categoryTemplates.reduce((sum, cat) => sum + (cat.benefits?.length || 0), 0)} benefits across {benefitDetailDialog.categoryTemplates.length} product categories
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-200px)] pr-4">
            <div className="space-y-6">
              {benefitDetailDialog.categoryTemplates.map((category: any) => (
                <Collapsible key={category.template_id} defaultOpen className="border rounded-lg p-4">
                  <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-accent/50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <h3 className="font-semibold text-sm">{category.category_name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {category.benefits?.length || 0} benefits
                      </Badge>
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    {category.category_path && (
                      <p className="text-xs text-muted-foreground mb-3 px-2">
                        {category.category_path}
                      </p>
                    )}
                    <div className="space-y-2">
                      {category.benefits && category.benefits
                        .sort((a: any, b: any) => a.priority_rank - b.priority_rank)
                        .map((benefit: any) => (
                          <div
                            key={benefit.benefit_name}
                            className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <Badge variant="secondary" className="text-xs font-mono">
                                #{benefit.priority_rank}
                              </Badge>
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm">{benefit.benefit_name}</h4>
                                <Badge variant="outline" className="text-xs">
                                  Weight: {benefit.priority_weight}
                                </Badge>
                              </div>
                              {benefit.benefit_description && (
                                <p className="text-xs text-muted-foreground">
                                  {benefit.benefit_description}
                                </p>
                              )}
                              {benefit.notes && (
                                <div className="mt-2 p-2 bg-muted/50 rounded text-xs italic">
                                  <MessageSquare className="h-3 w-3 inline mr-1" />
                                  {benefit.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Category Leaf Detail Panel */}
      {leafDetailPanel.category && (
        <CategoryLeafDetail
          category={leafDetailPanel.category}
          sectorId={sectorData?.id || 0}
          siteId={selectedSite?.id || selectedSite?._id}
          isOpen={leafDetailPanel.open}
          onClose={() => setLeafDetailPanel({ open: false, category: null })}
          onNavigateToTab={handleNavigateToTab}
          context="sector"
          onTriggerResearch={isSuperAdmin ? async (categoryName) => {
            // Trigger comprehensive research for this category
            try {
              const response = await authFetch(
                `${API_URL}/sectors/${sectorData.id}/categories/${leafDetailPanel.category?.id}/trigger-research`,
                { method: 'POST' }
              );

              if (response.ok) {
                const result = await response.json();
                toast({
                  title: 'Research Triggered!',
                  description: `Research initiated for "${categoryName}". Topic ID: ${result.data.topicId}. You can monitor progress in Master Topic Approvals.`,
                });
                // Reload category data to show the new research
                // The panel will auto-refresh via loadCategoryData
              } else {
                throw new Error('Failed to trigger research');
              }
            } catch (error) {
              console.error('Error triggering research:', error);
              toast({
                title: 'Error',
                description: 'Failed to trigger research. Please try again.',
                variant: 'destructive',
              });
            }
          } : undefined}
          onCategoriesCreated={() => {
            // Reload tree data to show newly created categories
            if (sectorData?.id) {
              loadTreeViewData(sectorData.id);
            }
            // categoryViewMode state is preserved, no need to change it
          }}
        />
      )}

      {/* Research Document Dialog */}
      <Dialog open={researchDialog.open} onOpenChange={(open) => !open && setResearchDialog({ open: false, topicId: null, research: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Research Document</DialogTitle>
            <DialogDescription>
              {researchDialog.research?.topic_name || 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {loadingResearchContent ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : researchDialog.research ? (
            <ScrollArea className="h-[70vh]">
              <div className="space-y-4 pr-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Sector</Label>
                    <p className="font-medium">{researchDialog.research.sector_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Research Date</Label>
                    <p className="font-medium">
                      {new Date(researchDialog.research.created_at).toLocaleString()}
                    </p>
                  </div>
                  {researchDialog.research.research_quality_score && (
                    <div>
                      <Label className="text-muted-foreground">Quality Score</Label>
                      <p className="font-medium">
                        {(researchDialog.research.research_quality_score * 100).toFixed(0)}%
                      </p>
                    </div>
                  )}
                </div>

                {researchDialog.research.research_summary && (
                  <div>
                    <Label className="text-muted-foreground">Summary</Label>
                    <Card className="mt-2 bg-muted/30">
                      <CardContent className="pt-4">
                        <p className="text-sm">{researchDialog.research.research_summary}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div>
                  <Label className="text-muted-foreground">Research Content</Label>
                  <Card className="mt-2 bg-muted/30">
                    <CardContent className="pt-4">
                      <div className="prose prose-sm dark:prose-invert max-w-none text-sm space-y-3">
                        {researchDialog.research.research_content
                          .split(/\\n\\n|\n\n/)
                          .filter((p: string) => p.trim())
                          .map((paragraph: string, idx: number) => {
                            // Handle bold markers **text**
                            const formattedText = paragraph
                              .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                              .replace(/\\n/g, '<br/>');
                            return (
                              <p
                                key={idx}
                                className="text-foreground leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: formattedText }}
                              />
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {researchDialog.research.key_findings && (
                  <div>
                    <Label className="text-muted-foreground">Key Findings</Label>
                    <div className="mt-2 space-y-2">
                      {Array.isArray(researchDialog.research.key_findings) ?
                        researchDialog.research.key_findings.map((finding: any, index: number) => (
                          <div key={index} className="text-sm bg-muted p-3 rounded-md">
                            {finding}
                          </div>
                        )) :
                        <pre className="text-sm bg-muted p-3 rounded-md">
                          {JSON.stringify(researchDialog.research.key_findings, null, 2)}
                        </pre>
                      }
                    </div>
                  </div>
                )}

                {researchDialog.research.suggested_followup_questions && researchDialog.research.suggested_followup_questions.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Suggested Follow-up Questions</Label>
                    <div className="mt-2 space-y-2">
                      {researchDialog.research.suggested_followup_questions.map((question: string, index: number) => (
                        <div key={index} className="text-sm bg-blue-50 dark:bg-blue-950 p-3 rounded-md flex items-start gap-2">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold">{index + 1}.</span>
                          <span>{question}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No research document found for this topic.
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResearchDialog({ open: false, topicId: null, research: null })}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// TreeNode Component for hierarchical category display
interface TreeNodeProps {
  node: any;
  level: number;
  expandedNodes: Set<string>;
  loadingNodes: Set<string>;
  onToggle: (id: string, node: any) => void;
  onTogglePrimary: (id: string, isPrimary: boolean) => void;
  onSchemaOrgUpdate: (id: string, schemaOrgType: string) => void;
  onLeafClick: (category: any) => void;
  isSuperAdmin: boolean;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  expandedNodes,
  loadingNodes,
  onToggle,
  onTogglePrimary,
  onSchemaOrgUpdate,
  onLeafClick,
  isSuperAdmin,
}) => {
  const [isEditingSchema, setIsEditingSchema] = React.useState(false);
  const [schemaOrgInput, setSchemaOrgInput] = React.useState(node.schema_org_type || '');
  const isExpanded = expandedNodes.has(node.id);
  const isLoading = loadingNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  // Check if can expand: has children OR has a google product category ID (for lazy loading)
  const canExpand = hasChildren || (node.google_product_category_id && node.level_depth < 3);
  const isLeaf = !canExpand; // True leaf: no children and no ability to lazy-load
  const indentPx = level * 24;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-2 px-3 hover:bg-muted/50 rounded-lg transition-colors group"
        style={{ paddingLeft: `${indentPx + 12}px` }}
      >
        {/* Expand/Collapse Icon */}
        {canExpand ? (
          <button
            onClick={() => onToggle(node.id, node)}
            className="p-1 hover:bg-muted rounded transition-colors"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            ) : isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        ) : (
          <div className="w-6" /> // Spacer for alignment
        )}

        {/* Category Name */}
        <span className="flex-1 font-medium text-sm">{node.name || node.category_name}</span>

        {/* Primary Badge/Toggle */}
        {isSuperAdmin ? (
          <button
            onClick={() => onTogglePrimary(node.id, !node.is_primary_level)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
              node.is_primary_level
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100'
            }`}
          >
            <Star className={`h-3 w-3 ${node.is_primary_level ? 'fill-current' : ''}`} />
            {node.is_primary_level && <span>Primary</span>}
          </button>
        ) : (
          node.is_primary_level && (
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
              <Star className="h-3 w-3 mr-1 fill-current" />
              Primary
            </Badge>
          )
        )}

        {/* Schema.org Badge/Input */}
        {isSuperAdmin ? (
          isEditingSchema ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={schemaOrgInput}
                onChange={(e) => setSchemaOrgInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSchemaOrgUpdate(node.id, schemaOrgInput);
                    setIsEditingSchema(false);
                  } else if (e.key === 'Escape') {
                    setSchemaOrgInput(node.schema_org_type || '');
                    setIsEditingSchema(false);
                  }
                }}
                placeholder="Schema.org type"
                className="px-2 py-1 text-xs border rounded w-32"
                autoFocus
              />
              <button
                onClick={() => {
                  onSchemaOrgUpdate(node.id, schemaOrgInput);
                  setIsEditingSchema(false);
                }}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                onClick={() => {
                  setSchemaOrgInput(node.schema_org_type || '');
                  setIsEditingSchema(false);
                }}
                className="text-gray-600 hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditingSchema(true)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                node.schema_org_type
                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Globe className="h-3 w-3" />
              {node.schema_org_type ? <span>{node.schema_org_type}</span> : <span>Schema</span>}
            </button>
          )
        ) : (
          node.schema_org_type && (
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              <Globe className="h-3 w-3 mr-1" />
              {node.schema_org_type}
            </Badge>
          )
        )}

        {/* Children Count Badge */}
        {hasChildren && (
          <Badge variant="outline" className="text-xs">
            {node.children.length}
          </Badge>
        )}

        {/* Level Depth Badge */}
        <Badge variant="secondary" className="text-xs">
          L{node.level_depth || level}
        </Badge>

        {/* Category Details Button - available for all categories */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onLeafClick(node)}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 h-7 px-2 text-xs bg-blue-50 hover:bg-blue-100 border-blue-300"
        >
          <Eye className="h-3 w-3" />
          Details
        </Button>
      </div>

      {/* Render Children */}
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children.map((child: any) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              loadingNodes={loadingNodes}
              onToggle={onToggle}
              onTogglePrimary={onTogglePrimary}
              onSchemaOrgUpdate={onSchemaOrgUpdate}
              onLeafClick={onLeafClick}
              isSuperAdmin={isSuperAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};